/*
 * CircuitSimUI — canvas editor for the /circuit-simulator tool.
 *
 * Depends on window.CircuitEngine (engine.js) for the DC solve and on optional page
 * globals (__ae, __toast, __toolSettings, __downloadFile, __announce). Reads localized
 * strings from window.CS_I18N (set inline by the blade). Pure browser code; no build step.
 *
 * Interaction (unified Pointer Events — mouse, touch, pen):
 *   - Palette button             -> add a component at the view centre
 *   - Drag a component body       -> move (snap to grid)
 *   - Press a terminal + drag     -> draw a wire to another terminal (works in any mode)
 *   - Wire mode + tap two pins    -> draw a wire (touch-friendly two-tap)
 *   - Tap a component or a wire    -> select it; a floating action bar (rotate / duplicate /
 *                                    delete) appears right on the canvas — the mobile-friendly
 *                                    way to DELETE a single component OR a single wire.
 *   - Drag empty space            -> pan; wheel / pinch -> zoom about the pointer
 *   - Double-tap a switch         -> toggle open/closed
 *   - Undo / Redo                 -> full history of edits
 *   - Simulate                    -> live DC solve: per-component V/I/P readouts, current-flow
 *                                    animation, LED brightness, lamp glow, motor spin, and
 *                                    friendly overload warnings (short / LED burnout / blown fuse).
 */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var T = window.CS_I18N || {};
  function t(k, d) { return (T && typeof T[k] === 'string') ? T[k] : (d != null ? d : k); }
  var CE = window.CircuitEngine;

  // Shared pure helpers (SimCore.util loads defer, before this file). Bail loudly if the
  // core is missing rather than throwing on first use with a cryptic ReferenceError.
  var SC = window.SimCore;
  if (!SC || !SC.util || !SC.camera || !SC.history || !SC.share || !SC.pointer || !SC.props || !SC.scope) { try { if (window.__ae) window.__ae('tool_error', { slug: 'circuit-simulator', name: 'sim-core-missing' }); } catch (e) {} return; }
  var esc = SC.util.esc, b64e = SC.util.b64e, b64d = SC.util.b64d, isLight = SC.util.isLight, rotXY = SC.util.rotXY;

  var GRID = 20, SPAN = 40;           // pin half-distance (pins 2*SPAN apart, multiple of GRID)
  var HIT_PIN = 15, HIT_WIRE = 9;     // screen-px hit radii
  var DPR = Math.min(window.devicePixelRatio || 1, 3);
  var MIN_S = SC.camera.MIN_S, MAX_S = SC.camera.MAX_S; // single source: SimCore.camera

  var COLORS = {
    grid: 'rgba(255,255,255,.05)', gridLight: 'rgba(0,0,0,.06)',
    wire: 'rgba(170,180,200,.9)', wireSel: '#38bdf8',
    pin: '#cbd5e1', pinHot: '#38bdf8', body: '#cbd5e1', sel: '#38bdf8',
    label: 'rgba(226,232,240,.95)', ref: '#93c5fd', read: '#34d399'
  };

  var DEFAULTS = {
    battery: { voltage: 9, rint: 0.5, color: '#f59e0b' },
    ac_source: { voltage: 10, freq: 50, phase: 0, rint: 0.5, color: '#f472b6' },
    bridge: { vf: 0.7, ron: 2, color: '#94a3b8' },
    transformer: { ratio: 2, color: '#a78bfa' },
    resistor: { resistance: 1000, color: '#10b981' },
    potentiometer: { resistance: 10000, wiper: 0.5, color: '#34d399' },
    led: { vf: 2.0, imax: 0.02, color: '#ef4444' },
    diode: { vf: 0.7, color: '#94a3b8' },
    capacitor: { capacitance: 100, color: '#3b82f6' },
    inductor: { inductance: 100, color: '#a78bfa' },
    switch: { closed: true, color: '#8b5cf6' },
    fuse: { imax: 1, blown: false, color: '#f43f5e' },
    current_source: { current: 0.02, color: '#22d3ee' },
    lamp: { resistance: 100, pmax: 1, color: '#fbbf24' },
    buzzer: { resistance: 100, color: '#eab308' },
    motor: { resistance: 50, color: '#38bdf8' },
    transistor: { kind: 'npn', hfe: 100, icmax: 0.5, pmax: 0.625, preset: 'custom', color: '#c084fc' },
    mosfet: { vth: 2, ron: 0.5, idmax: 5, vdsmax: 60, kind: 'nch', preset: 'custom', color: '#a3e635' },
    button: { pressed: false, color: '#8b5cf6' },
    photoresistor: { rmin: 1000, rmax: 200000, level: 0.5, color: '#facc15' },
    thermistor: { rmin: 1000, rmax: 50000, level: 0.5, color: '#fb923c' },
    relay: { rcoil: 100, pickup: 0.02, color: '#60a5fa' },
    opamp: { color: '#c084fc' },
    logic_gate: { kind: 'and', vdd: 5, color: '#2dd4bf' },
    logic_not: { vdd: 5, color: '#2dd4bf' },
    ne555: { color: '#f97316' },
    mcu: { program: 'blink', vdd: 5, interval: 500, pinModes: ['in', 'in', 'in', 'out', 'out', 'out'], outStates: [false, false, false, false, false, false], color: '#22c55e' },
    ground: { color: '#6b7280' },
    junction: { color: '#94a3b8' }
  };
  var PINCOUNT = { battery: 2, ac_source: 2, bridge: 4, transformer: 4, resistor: 2, led: 2, diode: 2, capacitor: 2, switch: 2, ground: 1, current_source: 2, potentiometer: 2, lamp: 2, inductor: 2, fuse: 2, buzzer: 2, motor: 2, transistor: 3, button: 2, photoresistor: 2, thermistor: 2, relay: 4, opamp: 3, logic_gate: 3, logic_not: 2, ne555: 8, mcu: 6, mosfet: 3, junction: 1 };
  var REFPREFIX = { battery: 'BAT', ac_source: 'AC', bridge: 'BR', transformer: 'T', resistor: 'R', led: 'LED', diode: 'D', capacitor: 'C', switch: 'SW', ground: 'GND', current_source: 'CS', potentiometer: 'P', lamp: 'LMP', inductor: 'L', fuse: 'F', buzzer: 'BZ', motor: 'M', transistor: 'Q', button: 'BTN', photoresistor: 'LDR', thermistor: 'TH', relay: 'K', opamp: 'U', logic_gate: 'U', logic_not: 'U', ne555: 'U', mcu: 'MCU', mosfet: 'Q' };
  var MCU_PROGRAMS = ['blink', 'follow', 'invert', 'and', 'toggle', 'sequence', 'counter'];
  // real-transistor presets (datasheet-typical hFE / Ic max / P max) — Ivan's catalog idea
  var TX_PRESETS = [
    { key: 'custom', kind: null, hfe: null, icmax: null, pmax: null },
    { key: 'КТ315Б', kind: 'npn', hfe: 70, icmax: 0.1, pmax: 0.15 },
    { key: 'КТ361Б', kind: 'pnp', hfe: 70, icmax: 0.05, pmax: 0.15 },
    { key: 'BC547', kind: 'npn', hfe: 200, icmax: 0.1, pmax: 0.5 },
    { key: '2N2222', kind: 'npn', hfe: 100, icmax: 0.6, pmax: 0.5 },
    { key: 'TIP31', kind: 'npn', hfe: 25, icmax: 3, pmax: 40 }
  ];
  // real-MOSFET presets (datasheet-typical Vth / Rds(on)) - same catalog idea as TX_PRESETS
  var MOS_PRESETS = [
    { key: 'custom', kind: null, vth: null, ron: null, idmax: null, vdsmax: null },
    { key: '2N7000', kind: 'nch', vth: 2.1, ron: 1.9, idmax: 0.2, vdsmax: 60 },
    { key: 'IRFZ44N', kind: 'nch', vth: 3, ron: 0.0175, idmax: 49, vdsmax: 55 },
    { key: 'AO3400', kind: 'nch', vth: 0.75, ron: 0.03, idmax: 5.7, vdsmax: 30 },
    { key: 'IRF9540N', kind: 'pch', vth: 3, ron: 0.117, idmax: 23, vdsmax: 100 },
    { key: 'AO3401', kind: 'pch', vth: 0.9, ron: 0.05, idmax: 4, vdsmax: 30 },
    { key: 'IRF4905', kind: 'pch', vth: 3, ron: 0.02, idmax: 74, vdsmax: 55 },
    // Power-N picks suggested by Ivan (Mr.Sanktum) — the SELECTION is his;
    // every figure below is from the part's DATASHEET. The measured 3V/5V drain
    // currents from the source video were deliberately dropped (05.08): that
    // table is another author's lab work, and we promised to build ours from
    // primary sources. «Gate from» renders via gateBadge(vth) instead.
    { key: 'IRF3704ZPBF', kind: 'nch', vth: 1.8, ron: 0.0079, idmax: 67, vdsmax: 20, group: 'sanktum' },
    { key: 'IRF3205PBF', kind: 'nch', vth: 3, ron: 0.008, idmax: 110, vdsmax: 55, group: 'sanktum' },
    { key: 'IRLB8743PBF', kind: 'nch', vth: 1.8, ron: 0.0032, idmax: 78, vdsmax: 30, group: 'sanktum' },
    { key: 'IRL2203NPBF', kind: 'nch', vth: 1.2, ron: 0.007, idmax: 116, vdsmax: 30, group: 'sanktum' },
    { key: 'IRLB8748PBF', kind: 'nch', vth: 1.8, ron: 0.0048, idmax: 92, vdsmax: 30, group: 'sanktum' },
    { key: 'IRF3704PBF', kind: 'nch', vth: 2, ron: 0.009, idmax: 77, vdsmax: 20, group: 'sanktum' },
    { key: 'IRL8113PBF', kind: 'nch', vth: 1.5, ron: 0.006, idmax: 105, vdsmax: 30, group: 'sanktum' },
    { key: 'IRL3803PBF', kind: 'nch', vth: 1.5, ron: 0.006, idmax: 140, vdsmax: 30, group: 'sanktum' },
    { key: 'IRLB3813PBF', kind: 'nch', vth: 1.8, ron: 0.00195, idmax: 260, vdsmax: 30, group: 'sanktum' },
    { key: 'IRL3502PBF', kind: 'nch', vth: 1.2, ron: 0.007, idmax: 110, vdsmax: 20, group: 'sanktum' },
    { key: 'IRL2505PBF', kind: 'nch', vth: 1.5, ron: 0.008, idmax: 104, vdsmax: 55, group: 'sanktum' },
    { key: 'IRF3711PBF', kind: 'nch', vth: 1.5, ron: 0.006, idmax: 110, vdsmax: 20, group: 'sanktum' },
    { key: 'IRL3713PBF', kind: 'nch', vth: 1.5, ron: 0.003, idmax: 260, vdsmax: 30, group: 'sanktum' },
    { key: 'IRF3709ZPBF', kind: 'nch', vth: 1.8, ron: 0.0063, idmax: 87, vdsmax: 30, group: 'sanktum' },
    { key: 'AUIRL3705N', kind: 'nch', vth: 1.5, ron: 0.0065, idmax: 89, vdsmax: 55, group: 'sanktum' },
    { key: 'IRLB3034PBF', kind: 'nch', vth: 1.6, ron: 0.0017, idmax: 195, vdsmax: 40, group: 'sanktum' },
    { key: 'IRF3711ZPBF', kind: 'nch', vth: 1.5, ron: 0.006, idmax: 92, vdsmax: 20, group: 'sanktum' }
  ];

  // pin offsets in the component's local (unrotated) frame; index = pin number
  var PIN_LOCAL = {
    ground: [{ x: 0, y: -SPAN, name: 'gnd' }],
    junction: [{ x: 0, y: 0, name: 'j' }],
    transistor: [{ x: SPAN, y: -SPAN, name: 'C' }, { x: -SPAN, y: 0, name: 'B' }, { x: SPAN, y: SPAN, name: 'E' }],
    potentiometer: [{ x: -SPAN, y: 0, name: '1' }, { x: SPAN, y: 0, name: '2' }, { x: 0, y: -20, name: 'W' }],
    bridge: [{ x: -SPAN, y: -18, name: '~1' }, { x: -SPAN, y: 18, name: '~2' }, { x: SPAN, y: -18, name: '+' }, { x: SPAN, y: 18, name: '−' }],
    transformer: [{ x: -SPAN, y: -18, name: 'P1' }, { x: -SPAN, y: 18, name: 'P2' }, { x: SPAN, y: -18, name: 'S1' }, { x: SPAN, y: 18, name: 'S2' }],
    relay: [{ x: -SPAN, y: -20, name: 'k+' }, { x: -SPAN, y: 20, name: 'k-' }, { x: SPAN, y: -20, name: 'c1' }, { x: SPAN, y: 20, name: 'c2' }],
    opamp: [{ x: -SPAN, y: -20, name: '+' }, { x: -SPAN, y: 20, name: '-' }, { x: SPAN, y: 0, name: 'out' }],
    logic_gate: [{ x: -SPAN, y: -20, name: 'a' }, { x: -SPAN, y: 20, name: 'b' }, { x: SPAN, y: 0, name: 'y' }],
    mcu: [{ x: -SPAN, y: -20, name: '1' }, { x: -SPAN, y: 0, name: '2' }, { x: -SPAN, y: 20, name: '3' }, { x: SPAN, y: -20, name: '4' }, { x: SPAN, y: 0, name: '5' }, { x: SPAN, y: 20, name: '6' }],
    // DIP-8 order like the physical chip (Ivan: "не 1,2,3,4 а 8,5,2,1"): left top-down
    // 1 GND, 2 TR, 3 OUT, 4 RST; right top-down 8 V+, 7 DIS, 6 TH, 5 CV.
    ne555: [{ x: -SPAN, y: -10, name: '2 TR' }, { x: SPAN, y: 10, name: '6 TH' }, { x: SPAN, y: -10, name: '7 DIS' }, { x: -SPAN, y: 10, name: '3 OUT' }, { x: SPAN, y: -30, name: '8 V+' }, { x: -SPAN, y: -30, name: '1 GND' }, { x: -SPAN, y: 30, name: '4 RST' }, { x: SPAN, y: 30, name: '5 CV' }],
    mosfet: [{ x: SPAN, y: -SPAN, name: 'D' }, { x: -SPAN, y: 0, name: 'G' }, { x: SPAN, y: SPAN, name: 'S' }],
    _def: [{ x: -SPAN, y: 0, name: 'p0' }, { x: SPAN, y: 0, name: 'p1' }]
  };
  // hit bounding boxes in local frame {ox,oy center offset, hw,hh half-extents}
  var BBOX = {
    ground: { ox: 0, oy: -SPAN / 2, hw: 18, hh: SPAN / 2 + 8 },
    junction: { ox: 0, oy: 0, hw: 9, hh: 9 },
    transistor: { ox: 6, oy: 0, hw: SPAN + 4, hh: SPAN + 6 },
    relay: { ox: 0, oy: 0, hw: SPAN, hh: 30 },
    opamp: { ox: 0, oy: 0, hw: SPAN - 2, hh: 26 },
    logic_gate: { ox: 0, oy: 0, hw: SPAN - 2, hh: 26 },
    mcu: { ox: 0, oy: 0, hw: SPAN - 2, hh: 30 },
    ne555: { ox: 0, oy: 0, hw: SPAN - 2, hh: 40 },
    mosfet: { ox: 6, oy: 0, hw: SPAN + 4, hh: SPAN + 6 },
    bridge: { ox: 0, oy: 0, hw: SPAN - 2, hh: 28 },
    transformer: { ox: 0, oy: 0, hw: SPAN - 2, hh: 28 },
    _def: { ox: 0, oy: 0, hw: SPAN - 2, hh: 16 }
  };


  // turns ratio N=Np/Ns shown as a clean colon form: 2 -> "2:1", 0.5 -> "1:2"
  function turnsLabel(n) { n = n || 1; return n >= 1 ? (Math.round(n * 100) / 100) + ':1' : '1:' + (Math.round(100 / n) / 100); }

  function defaultLabel(type, c) {
    if (!CE) CE = window.CircuitEngine;
    switch (type) {
      case 'battery': return (c.voltage != null ? c.voltage : 9) + 'V';
      case 'ac_source': return (c.voltage != null ? c.voltage : 10) + 'V ' + (c.freq != null ? c.freq : 50) + 'Hz';
      case 'bridge': return '';
      case 'transformer': return turnsLabel(c.ratio != null ? c.ratio : 2);
      case 'resistor': return CE ? CE.fmt.R(c.resistance != null ? c.resistance : 1000) : (c.resistance + 'Ω');
      case 'potentiometer': return CE ? CE.fmt.R(c.resistance != null ? c.resistance : 10000) : (c.resistance + 'Ω');
      case 'led': return t('comp_led', 'LED');
      case 'diode': return t('comp_diode', 'Diode');
      case 'capacitor': return (c.capacitance != null ? c.capacitance : 100) + 'µF';
      case 'inductor': return (c.inductance != null ? c.inductance : 100) + 'µH';
      case 'switch': return t('comp_switch', 'SW');
      case 'fuse': return (c.imax != null ? c.imax : 1) + 'A';
      case 'current_source': return CE ? CE.fmt.I(c.current != null ? c.current : 0.02) : (c.current + 'A');
      case 'lamp': return t('comp_lamp', 'Lamp');
      case 'buzzer': return t('comp_buzzer', 'Buzzer');
      case 'motor': return t('comp_motor', 'Motor');
      case 'transistor': return (c.kind === 'pnp' ? 'PNP' : 'NPN');
      case 'button': return t('comp_button', 'Button');
      case 'photoresistor': return t('comp_photoresistor', 'LDR');
      case 'thermistor': return t('comp_thermistor', 'Thermistor');
      case 'relay': return t('comp_relay', 'Relay');
      case 'opamp': return t('comp_opamp', 'Op-amp');
      case 'logic_gate': return (c.kind || 'and').toUpperCase();
      case 'logic_not': return 'NOT';
      case 'mcu': return t('comp_mcu', 'µC');
      case 'ne555': return '555';
      case 'mosfet': return c.kind === 'pch' ? 'P-MOSFET' : 'MOSFET';
      case 'ground': return t('comp_ground', 'GND');
      case 'junction': return '';
    }
    return type;
  }

  function CircuitSim() {
    this.components = [];
    this.wires = [];
    this.nextId = 1;
    this.cam = { x: 0, y: 0, s: 1 };
    this.mode = 'select';
    this.selected = null;        // component
    this.selectedWire = null;
    this.sim = null;             // last engine result
    this.simulating = false;
    this.completedOnce = false;
    this.pointers = {};          // pointerId -> {x,y} screen
    this.drag = null;            // {comp, ox, oy}
    this.wiring = null;          // {fromPin:{id,pin}, x,y}
    this.armedPin = null;        // wire two-tap first pin
    this.panning = null;
    this.pinch = null;
    this.hotPin = null;
    this.phase = 0;              // animation phase
    this.raf = 0;
    this.lastTapPin = null; this.lastTapT = 0;
    this._saveT = 0;
    this._undo = []; this._redo = [];
    this._propEditing = false;
    this._dragSnap = null;
    // oscilloscope (time-domain) — additive; DC "Simulate" is untouched
    this.scope = { open: false, running: false, probes: [], tsim: null, raf: 0, buf: [], tMax: 0, dt: 0.001, stepsPerFrame: 6 };
    this._scopeColors = ['#38bdf8', '#f59e0b', '#34d399', '#f472b6', '#a78bfa', '#fb7185'];
    this.ortho = false;          // orthogonal (right-angle) wire routing
    this._clipboard = null;      // copied component (JSON)
  }

  CircuitSim.prototype.init = function () {
    var self = this;
    var $ = function (id) { return document.getElementById(id); };
    this.$ = $;
    this.canvas = $('csCanvas');
    this.wrap = $('csCanvasWrap');
    this.actions = $('csActions');
    if (!this.canvas || !this.wrap || !CE) return;
    this.ctx = this.canvas.getContext('2d');

    this.ortho = true; // right-angle wires by default (Ivan: "провода — самое главное"); persisted opt-out
    // Symbol standard: GOST/IEC rectangle resistors for the RU audience (Ivan:
    // "обычно резистор это прямоугольник"), ANSI zigzag elsewhere; user-switchable.
    this.symStd = (window.__LOCALE === 'ru') ? 'gost' : 'ansi';
    try { if (window.__toolSettings) this.symStd = window.__toolSettings.load('circuit-simulator', 'symStd', this.symStd); } catch (e) {}
    try { if (window.__toolSettings) this.ortho = !!window.__toolSettings.load('circuit-simulator', 'ortho', true); } catch (e) {}
    this._ideal = false; // real model (battery Rint, diode Ron) by default; textbook/ideal is a persisted opt-in
    try { if (window.__toolSettings) this._ideal = !!window.__toolSettings.load('circuit-simulator', 'ideal', false); } catch (e) {}
    if (!this._loadFromUrl()) this._restore();   // ?c=<encoded> shared circuit wins over the saved one

    this._resize();
    if (this._shared) { this._fit(); this._hideHint(); }
    if (window.ResizeObserver) {
      var rt = 0;
      this._ro = new ResizeObserver(function () { clearTimeout(rt); rt = setTimeout(function () { self._resize(); }, 60); });
      this._ro.observe(this.wrap);
    }
    window.addEventListener('resize', function () { self._resize(); });

    this._bindPalette();
    this._bindToolbar();
    this._bindExamples();
    this._bindPointer();
    this._bindKeys();
    this._buildActions();
    this._bindScope();
    this._bindShare();
    this._bindShareBanner();
    if (SC.models && this._bindModels) this._bindModels();
    this._bindScrollbars();
    this._bindModelMode();
    this._updateUndoUI();
    this._updateOrthoUI();
    this._updateSymStdUI();

    this.draw();
    this._announceMode();
  };

  /* ---------------- persistence ---------------- */
  CircuitSim.prototype._persist = function () {
    if (this._sharedView) return; // non-destructive: don't overwrite the saved doc while only viewing a shared link
    var self = this; clearTimeout(this._saveT);
    this._saveT = setTimeout(function () {
      try {
        if (window.__toolSettings) {
          window.__toolSettings.save('circuit-simulator', 'circuit', JSON.stringify({
            components: self.components, wires: self.wires, nextId: self.nextId, cam: self.cam
          }));
        }
      } catch (e) {}
    }, 350);
  };
  CircuitSim.prototype._restore = function () {
    try {
      if (!window.__toolSettings) return;
      var raw = window.__toolSettings.load('circuit-simulator', 'circuit', null);
      if (!raw) return;
      var d = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (d && d.components && d.components.length) {
        this.components = d.components; this.wires = d.wires || []; this.nextId = d.nextId || (this.components.length + 1);
        this.components.forEach(function (c) { if (c.rot == null) c.rot = 0; if (!c.ref) c.ref = ''; });
        this._backfillRefs();
        if (d.cam) {
          var cs = +d.cam.s, cx = +d.cam.x, cy = +d.cam.y;
          this.cam = { x: isFinite(cx) ? cx : 0, y: isFinite(cy) ? cy : 0, s: (isFinite(cs) && cs >= MIN_S && cs <= MAX_S) ? cs : 1 };
        }
      }
    } catch (e) {}
  };
  CircuitSim.prototype._backfillRefs = function () {
    var self = this;
    this.components.forEach(function (c) { if (!c.ref) c.ref = self._nextRef(c.type); });
  };
  CircuitSim.prototype._nextRef = function (type) {
    if (type === 'junction') return ''; // bend points carry no reference label
    var pfx = REFPREFIX[type] || 'U', max = 0;
    this.components.forEach(function (c) {
      if (c.ref && c.ref.indexOf(pfx) === 0) { var n = parseInt(c.ref.slice(pfx.length), 10); if (isFinite(n) && n > max) max = n; }
    });
    return pfx + (max + 1);
  };

  /* ---------------- history (undo/redo) ---------------- */
  // history algorithm (_snapshot/_restoreSnap/_beginChange/undo/redo/_updateUndoUI): SimCore.history.
  // This tool supplies the serialize/restore seam + the after-hook + button ids:
  CircuitSim.prototype._undoBtnId = 'csUndoBtn';
  CircuitSim.prototype._redoBtnId = 'csRedoBtn';
  CircuitSim.prototype.serializeState = function () { return { components: this.components, wires: this.wires, nextId: this.nextId }; };
  CircuitSim.prototype.applyState = function (d) {
    this.components = d.components || []; this.wires = d.wires || []; this.nextId = d.nextId || (this.components.length + 1);
    this.selected = null; this.selectedWire = null; this.armedPin = null; this.wiring = null; this.drag = null;
  };
  CircuitSim.prototype._postHistory = function (which) {
    this._showProps(null); this._hideActions();
    if (this.components.length) this._hideHint(); else this._showHint();
    if (this.simulating || (this.scope && this.scope.running)) this._scheduleSim();
    this._updateUndoUI(); this._persist(); this.draw();
    this._ae('feature_use', { f: which });
  };

  /* ---------------- geometry ---------------- */
  CircuitSim.prototype.pins = function (c) {
    var base = PIN_LOCAL[c.type] || PIN_LOCAL._def, deg = c.rot || 0;
    return base.map(function (p) {
      var q = rotXY(p.x, p.y, deg);
      // lead exit direction (which way the wire should leave the pin), quantised to one axis so
      // the orthogonal router can steer around the package instead of across it. Radial from the
      // component centre = sign of the local pin position, rotated with the part; diagonal pins
      // (transistor C/E) fall back to horizontal, the way their leads are actually drawn.
      var d = rotXY(p.x < 0 ? -1 : (p.x > 0 ? 1 : 0), p.y < 0 ? -1 : (p.y > 0 ? 1 : 0), deg);
      var dx = 0, dy = 0;
      if (Math.abs(d.x) >= Math.abs(d.y)) dx = d.x < -0.01 ? -1 : (d.x > 0.01 ? 1 : 0);
      else dy = d.y < -0.01 ? -1 : 1;
      return { x: c.x + q.x, y: c.y + q.y, name: p.name, dx: dx, dy: dy };
    });
  };
  CircuitSim.prototype.pinPos = function (id, pin) {
    var c = this._byId(id); if (!c) return null;
    var ps = this.pins(c); return ps[pin] || null;
  };
  CircuitSim.prototype._byId = function (id) {
    for (var i = 0; i < this.components.length; i++) if (this.components[i].id === id) return this.components[i];
    return null;
  };
  CircuitSim.prototype.snap = function (v) { return Math.round(v / GRID) * GRID; }; // screenToWorld/worldToCanvas: SimCore.camera

  /* ---------------- resize ---------------- */
  CircuitSim.prototype._resize = function () {
    var w = this.wrap.clientWidth, h = this.wrap.clientHeight || 400;
    DPR = Math.min(window.devicePixelRatio || 1, 3);
    this.canvas.width = Math.round(w * DPR);
    this.canvas.height = Math.round(h * DPR);
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.cssW = w; this.cssH = h;
    this.draw();
  };

  /* ---------------- rendering ---------------- */
  CircuitSim.prototype.draw = function () {
    var ctx = this.ctx; if (!ctx) return;
    var light = isLight();
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, this.cssW, this.cssH);
    try { ctx.direction = 'ltr'; } catch (e) {} // designators/values/units are LTR notation even in RTL locales
    ctx.save();
    ctx.translate(this.cam.x, this.cam.y);
    ctx.scale(this.cam.s, this.cam.s);

    this._drawGrid(light);

    var vmin = 0, vmax = 0, haveV = false;
    if ((this.simulating || this.scope.running) && this.sim && this.sim.ok && this.sim.nodes) {
      for (var k in this.sim.nodes) { var v = this.sim.nodes[k]; if (!haveV) { vmin = vmax = v; haveV = true; } else { vmin = Math.min(vmin, v); vmax = Math.max(vmax, v); } }
    }
    this._vrange = { min: vmin, max: vmax, have: haveV };

    // unwired-pin highlight (Ivan: "кажется что подключён, а он не подключён")
    var wired = {};
    for (var wi = 0; wi < this.wires.length; wi++) { var ww = this.wires[wi]; wired[ww.a.id + '#' + ww.a.pin] = 1; wired[ww.b.id + '#' + ww.b.pin] = 1; }
    this._wiredPins = wired;

    this._drawWires(light);
    this._labelRects = [];   // fresh collision map for this frame's label layout
    for (var i = 0; i < this.components.length; i++) this._drawComponent(this.components[i], light);
    if (this.scope.probes.length) this._drawProbes();
    if (this.wiring) this._drawRubber();
    ctx.restore();
    this._positionActions();
    this._updateScrollbars();
    if (this.scope.open) this._drawScope();
    this._updateSelChip();
  };

  /**
   * Pinned readout chip for the SELECTED part — the pattern every leading
   * simulator uses (Falstad's info panel, EveryCircuit's top bar): the canvas
   * carries minimal text, the full live numbers live in one fixed, always
   * legible place. Ours sits over the canvas corner; created lazily so the
   * blade needs no markup.
   */
  CircuitSim.prototype._updateSelChip = function () {
    var chip = this._selChip;
    if (!chip) {
      var host = this.canvas && this.canvas.parentElement;
      if (!host) return;
      if (!/relative|absolute|fixed/.test(getComputedStyle(host).position)) host.style.position = 'relative';
      chip = this._selChip = document.createElement('div');
      chip.style.cssText = 'position:absolute;left:10px;bottom:10px;max-width:70%;padding:6px 10px;'
        + 'background:var(--glass,rgba(15,23,42,.82));border:1px solid var(--border,rgba(255,255,255,.14));'
        + 'border-radius:9px;font-size:12px;line-height:1.45;color:var(--text-primary,#e2e8f0);'
        + 'pointer-events:none;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);z-index:5;display:none';
      host.appendChild(chip);
    }
    var c = this.selected;
    if (!c || c.type === 'junction') { chip.style.display = 'none'; return; }
    var name = (c.ref ? c.ref + ' · ' : '') + (c.label != null && c.label !== '' ? c.label : defaultLabel(c.type, c));
    // same readout source and gate as the on-canvas label path
    var rc = ((this.simulating || this.scope.running) && this.sim && this.sim.ok && this.sim.components) ? this.sim.components[c.id] : null;
    var line = rc ? this._readoutLine(c, rc) : '';
    var txt = line ? name + ' — ' + line : name;
    if (txt !== this._selChipTxt) {
      this._selChipTxt = txt;
      chip.textContent = txt;
      chip.style.color = rc && (rc.overload || rc.willBlow) ? '#f87171' : 'var(--text-primary,#e2e8f0)';
    }
    chip.style.display = '';
  };
  CircuitSim.prototype._drawProbes = function () {
    var ctx = this.ctx;
    for (var i = 0; i < this.scope.probes.length; i++) {
      var p = this.scope.probes[i], pos = this.pinPos(p.id, p.pin); if (!pos) continue;
      ctx.save(); ctx.translate(pos.x, pos.y);
      ctx.strokeStyle = p.color; ctx.lineWidth = 2 / this.cam.s;
      ctx.beginPath(); ctx.arc(0, 0, 7 / this.cam.s, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(0, 0, 2.5 / this.cam.s, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  };

  CircuitSim.prototype._drawGrid = function (light) {
    var ctx = this.ctx;
    var x0 = (-this.cam.x) / this.cam.s, y0 = (-this.cam.y) / this.cam.s;
    var x1 = x0 + this.cssW / this.cam.s, y1 = y0 + this.cssH / this.cam.s;
    if (!isFinite(x0) || !isFinite(x1) || !isFinite(y0) || !isFinite(y1)) return;
    var sx = Math.floor(x0 / GRID) * GRID, sy = Math.floor(y0 / GRID) * GRID;
    ctx.strokeStyle = light ? COLORS.gridLight : COLORS.grid; ctx.lineWidth = 1 / this.cam.s;
    ctx.beginPath();
    for (var x = sx; x <= x1; x += GRID) { ctx.moveTo(x, y0); ctx.lineTo(x, y1); }
    for (var y = sy; y <= y1; y += GRID) { ctx.moveTo(x0, y); ctx.lineTo(x1, y); }
    ctx.stroke();
  };

  CircuitSim.prototype._nodeColor = function (node) {
    var vr = this._vrange;
    if (!vr || !vr.have || vr.max - vr.min < 1e-6) return null;
    if (this.sim && this.sim.datum === node) return '#64748b';
    var v = (this.sim.nodes[node] || 0);
    var t01 = (v - vr.min) / (vr.max - vr.min);
    var r = Math.round(60 + t01 * 195), g = Math.round(120 + (1 - Math.abs(t01 - 0.5) * 2) * 100), b = Math.round(255 - t01 * 195);
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  };

  // wire route as a polyline of >=2 points. Orthogonal mode = a SINGLE right-angle elbow
  // (L-route), not the old H-V-H "Z" with a vertical jog floating mid-span (Ivan: the wire
  // must go cleanly by right angles). Two elbows are possible — horizontal-first (corner at the
  // target column) or vertical-first (corner at the source column). We leave the source pin
  // ALONG its lead: a left/right pin goes horizontal-first, an up/down pin vertical-first —
  // UNLESS the far end sits behind the pin, where leaving along the lead would drag the wire
  // straight back across its own package. Then we turn perpendicular first, stepping the wire
  // off the body before it heads across. Horizontal-first dragging a pin's wire back over its
  // part is exactly what made two different-net wires overlap on it and disappear under the
  // symbol (Ivan 2026-07-23: "невидимые провода в режиме L, отключишь L — всё видно").
  CircuitSim.prototype._wirePath = function (a, b, o) {
    // per-wire routing (Ivan: "хочется какой-то оставить косым"): a wire remembers the mode
    // it was drawn in (w.o); the global toggle only sets the mode for NEW wires. Legacy
    // wires without the flag keep following the global toggle as before.
    var ortho = (o != null) ? !!o : this.ortho;
    if (!ortho || Math.abs(a.x - b.x) < 0.5 || Math.abs(a.y - b.y) < 0.5) return [a, b];
    var horizFirst;
    if (a.dx) horizFirst = (b.x - a.x) * a.dx >= 0;      // faces left/right: along the lead when the target is ahead
    else if (a.dy) horizFirst = (b.y - a.y) * a.dy < 0;  // faces up/down: vertical-first when the target is ahead
    else horizFirst = true;
    return horizFirst ? [a, { x: b.x, y: a.y }, b] : [a, { x: a.x, y: b.y }, b];
  };
  function polyStroke(ctx, path) { ctx.beginPath(); ctx.moveTo(path[0].x, path[0].y); for (var k = 1; k < path.length; k++) ctx.lineTo(path[k].x, path[k].y); ctx.stroke(); }
  CircuitSim.prototype._drawWires = function (light) {
    var ctx = this.ctx;
    for (var i = 0; i < this.wires.length; i++) {
      var w = this.wires[i];
      var a = this.pinPos(w.a.id, w.a.pin), b = this.pinPos(w.b.id, w.b.pin);
      if (!a || !b) continue;
      var path = this._wirePath(a, b, w.o);
      var sel = this.selectedWire === w;
      var col = COLORS.wire;
      var live = (this.simulating || this.scope.running);
      if (sel) col = COLORS.wireSel;
      else if (live && this.sim && this.sim.ok && this.sim.pinNodes) {
        var nodeC = this._nodeColor(this.sim.pinNodes[w.a.id + '#' + w.a.pin]);
        if (nodeC) col = nodeC;
      }
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      if (sel) { ctx.strokeStyle = 'rgba(56,189,248,.28)'; ctx.lineWidth = 8 / this.cam.s; polyStroke(ctx, path); }
      ctx.strokeStyle = col; ctx.lineWidth = (sel ? 3.5 : 2.5) / this.cam.s;
      polyStroke(ctx, path);
      if (live && this.sim && this.sim.ok) this._flowDots(path, this._wireCurrent(w));
    }
    this._drawNodeDots(light);
  };

  // Connection dots — the standard schematic convention: a filled dot marks a junction where
  // three or more conductors meet. The component's own lead is one conductor, so a pin with
  // TWO wires attached is already a T-junction and gets a dot; one wire is a plain
  // continuation and gets none. Without this, right-angle routing can lay one wire's segment
  // along another's and the branch looks like it hangs in mid-air (Ivan: «видите соединение на
  // стыке R2, R1 и Q1? там есть невидимый провод… ощущение что оно не присоединено»).
  CircuitSim.prototype._drawNodeDots = function (light) {
    var cnt = {}, i, w, k;
    for (i = 0; i < this.wires.length; i++) {
      w = this.wires[i];
      k = w.a.id + '#' + w.a.pin; cnt[k] = (cnt[k] || 0) + 1;
      k = w.b.id + '#' + w.b.pin; cnt[k] = (cnt[k] || 0) + 1;
    }
    var ctx = this.ctx, r = 4 / this.cam.s;
    ctx.fillStyle = light ? '#334155' : '#cbd5e1';
    for (k in cnt) {
      if (cnt[k] < 2) continue;
      var sep = k.lastIndexOf('#');
      var c = this._byId(k.slice(0, sep));
      if (!c || c.type === 'junction') continue; // a junction already draws its own dot
      var p = this.pinPos(c.id, +k.slice(sep + 1));
      if (!p) continue;
      ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
    }
    this._drawOpenPins();
  };

  // Red rings on pins with NO wire attached. «Нет замкнутого контура» told the user something
  // was wrong but not WHERE, so they had to hunt the break by eye (Ivan asked twice: «неподключённые
  // пины подсвечивать красным… пользователь вынужден гадать, где разрыв»). Only shown while
  // simulating and only when the solve actually failed or reported an open circuit — a half-built
  // circuit you are still wiring must not be painted red.
  CircuitSim.prototype._drawOpenPins = function () {
    if (!this.simulating || !this.sim) return;
    var warn = this.sim.warnings || [];
    var bad = !this.sim.ok || warn.indexOf('open_circuit') >= 0 || warn.indexOf('incomplete_circuit') >= 0;
    if (!bad) return;
    var wired = {}, i, w;
    for (i = 0; i < this.wires.length; i++) {
      w = this.wires[i];
      wired[w.a.id + '#' + w.a.pin] = 1; wired[w.b.id + '#' + w.b.pin] = 1;
    }
    var ctx = this.ctx, r = 7 / this.cam.s;
    ctx.strokeStyle = '#dc2626'; ctx.lineWidth = 2 / this.cam.s;
    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      if (c.type === 'junction') continue;
      var pc = PINCOUNT[c.type] || 2;
      for (var p2 = 0; p2 < pc; p2++) {
        if (wired[c.id + '#' + p2]) continue;
        var pos = this.pinPos(c.id, p2);
        if (!pos) continue;
        ctx.beginPath(); ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2); ctx.stroke();
      }
    }
  };

  // components that still have a free pin — named in the error line so the user reads
  // "connect these" instead of hunting the break
  CircuitSim.prototype._openPinRefs = function () {
    var wired = {}, i, w, out = [];
    for (i = 0; i < this.wires.length; i++) {
      w = this.wires[i];
      wired[w.a.id + '#' + w.a.pin] = 1; wired[w.b.id + '#' + w.b.pin] = 1;
    }
    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      if (c.type === 'junction') continue;
      var pc = PINCOUNT[c.type] || 2;
      for (var p2 = 0; p2 < pc; p2++) {
        if (!wired[c.id + '#' + p2]) { out.push(c.ref || c.type); break; }
      }
    }
    return out;
  };

  // current exiting comp a at its pin into the wire (toward b). Uses the engine's per-pin currents.
  CircuitSim.prototype._wireCurrent = function (w) {
    if (!this.sim) return 0;
    if (this.sim.pinCurrents) { var pc = this.sim.pinCurrents[w.a.id + '#' + w.a.pin]; if (typeof pc === 'number') return pc; }
    var rc = this.sim.components && this.sim.components[w.a.id];
    if (!rc || typeof rc.branchCurrent !== 'number') return 0;
    return (w.a.pin === 0 ? -rc.branchCurrent : rc.branchCurrent);
  };

  CircuitSim.prototype._flowDots = function (path, cur) {
    if (Math.abs(cur) < 1e-7) return;
    var ctx = this.ctx, dir = cur > 0 ? 1 : -1;
    var segs = [], total = 0;
    for (var k = 0; k < path.length - 1; k++) { var dx = path[k + 1].x - path[k].x, dy = path[k + 1].y - path[k].y, l = Math.hypot(dx, dy); segs.push({ x: path[k].x, y: path[k].y, ux: l ? dx / l : 0, uy: l ? dy / l : 0, l: l, off: total }); total += l; }
    if (total < 1) return;
    var mag = Math.min(1, Math.abs(cur) / 0.02);
    var gap = 26, speed = (this.phase * (0.6 + mag * 1.6)) % gap;
    ctx.fillStyle = '#fde68a';
    for (var d = speed; d < total; d += gap) {
      var dd = dir > 0 ? d : (total - d);
      for (var s = 0; s < segs.length; s++) { var sg = segs[s]; if (dd >= sg.off && dd <= sg.off + sg.l) { var lo = dd - sg.off; ctx.beginPath(); ctx.arc(sg.x + sg.ux * lo, sg.y + sg.uy * lo, 2.4 / this.cam.s, 0, Math.PI * 2); ctx.fill(); break; } }
    }
  };

  CircuitSim.prototype._drawComponent = function (c, light) {
    var ctx = this.ctx;
    var rc = ((this.simulating || this.scope.running) && this.sim && this.sim.ok && this.sim.components) ? this.sim.components[c.id] : null;
    var bodyCol = light ? '#334155' : (c.color || COLORS.body);
    var lead = light ? '#475569' : '#94a3b8';
    var deg = c.rot || 0;

    // --- symbol + leads in the rotated local frame ---
    ctx.save(); ctx.translate(c.x, c.y); ctx.rotate(deg * Math.PI / 180);

    if (this.selected === c) {
      var bb = BBOX[c.type] || BBOX._def;
      ctx.strokeStyle = COLORS.sel; ctx.lineWidth = 1.5 / this.cam.s; ctx.setLineDash([5 / this.cam.s, 4 / this.cam.s]);
      this._roundRect(ctx, bb.ox - bb.hw - 4, bb.oy - bb.hh - 4, (bb.hw + 4) * 2, (bb.hh + 4) * 2, 8 / this.cam.s); ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead; ctx.fillStyle = bodyCol; ctx.lineCap = 'round';

    if (c.type === 'junction') { ctx.fillStyle = bodyCol; ctx.beginPath(); ctx.arc(0, 0, 4.5, 0, Math.PI * 2); ctx.fill(); }
    else if (c.type === 'ground') this._drawGround(ctx, bodyCol, lead);
    else if (c.type === 'transistor') this._drawTransistor(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'relay') this._drawRelay(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'opamp') this._drawOpamp(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'logic_gate' || c.type === 'logic_not') this._drawLogic(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'mcu') this._drawMcu(ctx, c, rc, bodyCol, lead, light);
    else if (c.type === 'ne555') this._drawNe555(ctx, c, rc, bodyCol, lead, light);
    else if (c.type === 'mosfet') this._drawMosfet(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'bridge') this._drawBridge(ctx, c, rc, bodyCol, lead);
    else if (c.type === 'transformer') this._drawTransformer(ctx, c, rc, bodyCol, lead);
    else {
      ctx.strokeStyle = lead;
      ctx.beginPath(); ctx.moveTo(-SPAN, 0); ctx.lineTo(-14, 0); ctx.moveTo(14, 0); ctx.lineTo(SPAN, 0); ctx.stroke();
      ctx.strokeStyle = bodyCol;
      if (c.type === 'battery') this._drawBattery(ctx);
      else if (c.type === 'ac_source') this._drawAcSource(ctx);
      else if (c.type === 'resistor') this._drawResistor(ctx);
      else if (c.type === 'potentiometer') this._drawPot(ctx, c);
      else if (c.type === 'led') this._drawLed(ctx, c, rc);
      else if (c.type === 'diode') this._drawDiode(ctx, bodyCol);
      else if (c.type === 'capacitor') this._drawCapacitor(ctx);
      else if (c.type === 'inductor') this._drawInductor(ctx);
      else if (c.type === 'switch') this._drawSwitch(ctx, c);
      else if (c.type === 'fuse') this._drawFuse(ctx, c, rc);
      else if (c.type === 'current_source') this._drawCurrentSource(ctx, c);
      else if (c.type === 'lamp') this._drawLamp(ctx, c, rc);
      else if (c.type === 'buzzer') this._drawBuzzer(ctx, rc);
      else if (c.type === 'motor') this._drawMotor(ctx, rc);
      else if (c.type === 'button') this._drawButton(ctx, c, rc);
      else if (c.type === 'photoresistor') this._drawPhoto(ctx, c);
      else if (c.type === 'thermistor') this._drawThermistor(ctx, c);
    }
    ctx.restore();

    // --- terminals at world pin positions (unrotated frame) ---
    var ps = this.pins(c);
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      var hot = this.hotPin && this.hotPin.id === c.id && this.hotPin.pin === i;
      var armed = this.armedPin && this.armedPin.id === c.id && this.armedPin.pin === i;
      var unwired = this.components.length > 1 && !(this._wiredPins && this._wiredPins[c.id + '#' + i]);
      ctx.save(); ctx.translate(p.x, p.y);
      if (unwired && !hot && !armed) { // amber ring = this terminal has NO wire yet
        ctx.strokeStyle = 'rgba(251,146,60,.9)'; ctx.lineWidth = 1.6 / this.cam.s;
        ctx.beginPath(); ctx.arc(0, 0, 6 / this.cam.s, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#fb923c';
      } else ctx.fillStyle = (hot || armed) ? COLORS.pinHot : COLORS.pin;
      ctx.beginPath(); ctx.arc(0, 0, (hot || armed ? 5.5 : 3.5) / this.cam.s, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    // --- label (ref + value) + readout, upright ---
    // Greedy de-overlap: at phone zoom the 11px-on-screen text is LARGE in world
    // units while parts sit close, so neighbouring labels used to print over each
    // other into mush (tester's screenshot, 31.07). Each label block now checks
    // the rects already placed this frame and slides down below the collision.
    ctx.save(); ctx.translate(c.x, c.y);
    var below = ((BBOX[c.type] || BBOX._def).hh) + 14;
    ctx.textAlign = 'center';
    var refTxt = c.ref ? c.ref + ' ' : '';
    var valTxt = c.label != null && c.label !== '' ? c.label : defaultLabel(c.type, c);
    ctx.font = (11 / this.cam.s) + 'px sans-serif';
    // Zoom-aware detail, the way the leading simulators do it (Falstad/EveryCircuit:
    // minimal text on canvas, full numbers in a pinned panel for the SELECTED part).
    // Zoomed out on a phone the 11-screen-px strings dwarf the parts; per-part
    // readouts drop first, then values — the ref alone still identifies the part,
    // and the selected part's full numbers always live in the pinned chip below.
    var zoomedOut = this.cam.s < 0.75, farOut = this.cam.s < 0.5;
    var text1 = farOut ? (c.ref || valTxt) : (refTxt ? refTxt + valTxt : valTxt);
    var line = (rc && !zoomedOut) ? this._readoutLine(c, rc) : '';
    var lineGap = 13 / this.cam.s;   // was a fixed 13 world px — self-overlapped when zoomed out
    var lw = Math.max(ctx.measureText(text1).width, line ? ctx.measureText(line).width : 0);
    var lh = (line ? 2 : 1) * lineGap;
    var rects = this._labelRects || (this._labelRects = []);
    var rx = c.x - lw / 2, ry = c.y + below - lineGap * 0.85, guard = 0;
    for (var ri = 0; ri < rects.length && guard < 30; ri++) {
      var R = rects[ri];
      if (rx < R.x + R.w && rx + lw > R.x && ry < R.y + R.h && ry + lh > R.y) {
        var shift = (R.y + R.h) - ry + 2 / this.cam.s;
        below += shift; ry += shift; guard++; ri = -1;   // restart the scan from the top
      }
    }
    rects.push({ x: rx, y: ry, w: lw, h: lh });
    if (refTxt) { ctx.fillStyle = COLORS.ref; ctx.fillText(text1, 0, below); }
    else { ctx.fillStyle = COLORS.label; ctx.fillText(text1, 0, below); }
    if (line) { ctx.fillStyle = rc.overload || rc.willBlow ? '#f87171' : COLORS.read; ctx.fillText(line, 0, below + lineGap); }
    ctx.restore();
  };

  CircuitSim.prototype._roundRect = function (ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  };

  CircuitSim.prototype._readoutLine = function (c, rc) {
    if (!CE) return '';
    switch (c.type) {
      case 'ground': case 'junction': return '';
      case 'resistor': return CE.fmt.I(rc.current) + ' · ' + CE.fmt.V(rc.voltage);
      // potentiometer leads with the EFFECTIVE resistance the wiper currently sets, so dragging
      // the slider shows "at this resistance — this current, this voltage" live (Ivan: «очень
      // важно… смотришь при каком сопротивлении какой ток»). Matches the breadboard readout.
      case 'potentiometer': return CE.fmt.R(rc.resistanceEff != null ? rc.resistanceEff : (+c.resistance || 0) * (+c.wiper || 0))
        + ' · ' + CE.fmt.I(rc.current) + ' · ' + CE.fmt.V(rc.voltage);
      case 'ac_source': return CE.fmt.V(rc.emf != null ? rc.emf : rc.voltage) + ' · ' + CE.fmt.I(rc.current);
      case 'bridge': return CE.fmt.V(rc.voltage) + ' · ' + CE.fmt.I(rc.current);
      case 'transformer': return CE.fmt.V(rc.vSecondary) + ' · ' + CE.fmt.I(Math.abs(rc.iSecondary));
      case 'battery': return CE.fmt.I(rc.current) + (Math.abs(rc.voltage - rc.emf) > 0.05 ? ' · ' + CE.fmt.V(rc.voltage) : '');
      case 'current_source': return CE.fmt.I(rc.current) + ' · ' + CE.fmt.V(rc.voltage);
      case 'led': return rc.state === 'on' ? (CE.fmt.I(rc.current) + (rc.overload ? ' ⚠' : '')) : t('state_off', 'off');
      case 'diode': return rc.state === 'on' ? CE.fmt.I(rc.current) : t('state_off', 'off');
      case 'capacitor': return CE.fmt.V(rc.voltage);
      case 'inductor': return CE.fmt.I(rc.current);
      case 'fuse': return rc.state === 'blown' ? t('state_blown', 'blown') : (CE.fmt.I(rc.current) + (rc.willBlow ? ' ⚠' : ''));
      case 'lamp': return rc.active ? (CE.fmt.P(rc.power)) : t('state_off', 'off');
      case 'buzzer': case 'motor': return rc.active ? CE.fmt.I(rc.current) : t('state_off', 'off');
      case 'switch': return rc.state === 'closed' ? CE.fmt.I(rc.current) : t('state_open', 'open');
      case 'transistor': return rc.state === 'on' ? ('Ic ' + CE.fmt.I(rc.ic) + (rc.region === 'active' ? ' · ' + t('tx_active', 'active') : '') + ((rc.overload || rc.hot) ? ' ⚠' : '')) : t('state_off', 'off');
      case 'mosfet': return rc.state === 'on' ? ('Id ' + CE.fmt.I(rc.current) + ((rc.overload || rc.overvolt) ? ' ⚠' : '')) : (t('state_off', 'off') + (rc.overvolt ? ' ⚠' : ''));
      case 'button': return rc.state === 'pressed' ? CE.fmt.I(rc.current) : t('state_off', 'off');
      case 'photoresistor': case 'thermistor': return CE.fmt.R(rc.resistanceEff) + ' · ' + CE.fmt.I(rc.current);
      case 'relay': return (rc.energized ? t('state_closed', 'closed') : t('state_open', 'open')) + ' · ' + CE.fmt.I(rc.coilCurrent);
      case 'opamp': return CE.fmt.V(rc.vout);
      case 'logic_gate': case 'logic_not': return rc.logicHigh ? '1 · ' + CE.fmt.V(rc.voltage) : '0';
      case 'ne555': return rc.state === 'hi' ? 'OUT 1 · ' + CE.fmt.V(rc.voltage) : 'OUT 0';
      case 'mcu': return t('mcu_prog_' + (c.program || 'blink'), c.program || 'blink');
    }
    return '';
  };

  /* ---------------- component symbols (local frame) ---------------- */
  // AC source: the IEC circle with a sine inside — instantly readable next to the battery.
  CircuitSim.prototype._drawAcSource = function (ctx) {
    ctx.lineWidth = 2 / this.cam.s;
    ctx.beginPath(); ctx.arc(0, 0, 13, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath();
    for (var i = -8; i <= 8; i++) { var y = -Math.sin(i / 8 * Math.PI) * 6; if (i === -8) ctx.moveTo(i, y); else ctx.lineTo(i, y); }
    ctx.stroke();
  };
  // Full-wave bridge: the classic diamond with four diodes, ~ on the AC corners, +/− on the DC side.
  CircuitSim.prototype._drawBridge = function (ctx, c, rc, bodyCol, lead) {
    var R = 26;
    ctx.strokeStyle = lead; ctx.lineWidth = 2 / this.cam.s;
    // leads from the four pins to the diamond corners
    ctx.beginPath();
    ctx.moveTo(-SPAN, -18); ctx.lineTo(-R, 0); ctx.moveTo(-SPAN, 18); ctx.lineTo(0, R);
    ctx.moveTo(SPAN, -18); ctx.lineTo(0, -R); ctx.moveTo(SPAN, 18); ctx.lineTo(R, 0);
    ctx.stroke();
    ctx.strokeStyle = bodyCol;
    ctx.beginPath(); ctx.moveTo(-R, 0); ctx.lineTo(0, -R); ctx.lineTo(R, 0); ctx.lineTo(0, R); ctx.closePath(); ctx.stroke();
    // four diode triangles pointing towards the + corner / away from the − corner
    var lit = rc && rc.state === 'on';
    ctx.fillStyle = lit ? bodyCol : 'rgba(148,163,184,.55)';
    var tri = [[-13, -13, 1, 1], [13, -13, -1, 1], [-13, 13, 1, -1], [13, 13, -1, -1]];
    for (var k = 0; k < tri.length; k++) {
      var x = tri[k][0], y = tri[k][1], dx = tri[k][2], dy = tri[k][3];
      ctx.beginPath();
      ctx.moveTo(x - dx * 5, y - dy * 5);
      ctx.lineTo(x + dx * 5 - dy * 4, y + dy * 5 - dx * 4);
      ctx.lineTo(x + dx * 5 + dy * 4, y + dy * 5 + dx * 4);
      ctx.closePath(); ctx.fill();
    }
    ctx.fillStyle = bodyCol; ctx.font = (10 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('~', -R - 7, -14); ctx.fillText('~', -R - 7, 22);
    ctx.fillText('+', R + 7, -14); ctx.fillText('−', R + 7, 22);
  };
  // Ideal transformer: two vertical windings (primary left, secondary right) sharing an iron core
  // (the twin centre lines). Bump count hints the turns ratio direction (more bumps = more turns).
  CircuitSim.prototype._drawTransformer = function (ctx, c, rc, bodyCol, lead) {
    var yTop = -15, yBot = 15, r = 5;
    var N = rc && rc.ratio != null ? rc.ratio : (c.ratio != null ? c.ratio : 2);
    var pBumps = N >= 1 ? 4 : 3, sBumps = N >= 1 ? 3 : 4;   // step-down: more primary turns
    ctx.lineWidth = 2 / this.cam.s;
    // leads from the four pins to the winding ends
    ctx.strokeStyle = lead;
    ctx.beginPath();
    ctx.moveTo(-SPAN, -18); ctx.lineTo(-9, yTop); ctx.moveTo(-SPAN, 18); ctx.lineTo(-9, yBot);
    ctx.moveTo(SPAN, -18); ctx.lineTo(9, yTop); ctx.moveTo(SPAN, 18); ctx.lineTo(9, yBot);
    ctx.stroke();
    // energised tint when secondary carries current
    var live = rc && Math.abs(rc.iSecondary || 0) > 1e-9;
    ctx.strokeStyle = live ? bodyCol : 'rgba(148,163,184,.85)';
    // primary winding — bumps bulge left
    ctx.beginPath();
    for (var i = 0; i < pBumps; i++) { var cy = yTop + (yBot - yTop) * (i + 0.5) / pBumps; ctx.moveTo(-9, cy - r); ctx.arc(-9, cy, r, -Math.PI / 2, Math.PI / 2, true); }
    ctx.stroke();
    // secondary winding — bumps bulge right
    ctx.beginPath();
    for (var j = 0; j < sBumps; j++) { var sy = yTop + (yBot - yTop) * (j + 0.5) / sBumps; ctx.moveTo(9, sy - r); ctx.arc(9, sy, r, -Math.PI / 2, Math.PI / 2, false); }
    ctx.stroke();
    // iron core: twin vertical lines between the windings
    ctx.strokeStyle = bodyCol;
    ctx.beginPath(); ctx.moveTo(-2.5, yTop - 3); ctx.lineTo(-2.5, yBot + 3); ctx.moveTo(2.5, yTop - 3); ctx.lineTo(2.5, yBot + 3); ctx.stroke();
  };
  CircuitSim.prototype._drawBattery = function (ctx) {
    ctx.lineWidth = 2 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(-5, -12); ctx.lineTo(-5, 12); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(5, -7); ctx.lineTo(5, 7); ctx.stroke();
    ctx.font = (11 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.fillStyle = ctx.strokeStyle;
    ctx.fillText('+', -12, -8);
  };
  CircuitSim.prototype._drawResistor = function (ctx) {
    if (this.symStd === 'gost') {
      // GOST / IEC 60617: the resistor is a plain rectangle
      ctx.beginPath(); ctx.rect(-14, -6, 28, 12); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.moveTo(-14, 0); ctx.lineTo(-12, -7); ctx.lineTo(-8, 7); ctx.lineTo(-4, -7); ctx.lineTo(0, 7); ctx.lineTo(4, -7); ctx.lineTo(8, 7); ctx.lineTo(12, -7); ctx.lineTo(14, 0); ctx.stroke();
    }
  };
  CircuitSim.prototype._drawPot = function (ctx, c) {
    this._drawResistor(ctx);
    // wiper arrow — now a REAL third pin at (0,-20), not just decoration
    ctx.beginPath(); ctx.moveTo(0, -20); ctx.lineTo(0, -3); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-3, -7); ctx.lineTo(0, -3); ctx.lineTo(3, -7); ctx.stroke();
  };
  CircuitSim.prototype._drawLed = function (ctx, c, rc) {
    // Brightness follows the CURRENT, like a real LED: below ~2% of the rated
    // current the die is dark (Ivan: "светит одинаково и при 6 мА и при <1 мА" —
    // the multivibrator's off-side LED carries a fraction of base current and was
    // drawn at full colour). The body fill ramps with brightness, not on/off.
    var vis = rc ? Math.abs(rc.current || 0) / (rc.imax || 0.02) : 0;
    var on = rc && rc.state === 'on' && vis > 0.02;
    if (on) {
      var b = Math.min(1, vis);
      var pulse = 0.75 + 0.25 * Math.sin(this.phase * 0.25);
      ctx.save(); ctx.globalAlpha = Math.min(0.55, 0.3 * b * pulse + (rc.overload ? 0.2 : 0)); ctx.fillStyle = rc.overload ? '#fca5a5' : (c.color || '#ef4444');
      ctx.beginPath(); ctx.arc(0, 0, 17 * (0.5 + 0.5 * b), 0, Math.PI * 2); ctx.fill(); ctx.restore();
    }
    ctx.beginPath(); ctx.moveTo(-9, -9); ctx.lineTo(-9, 9); ctx.lineTo(9, 0); ctx.closePath();
    if (on) { ctx.save(); ctx.globalAlpha = 0.35 + 0.65 * Math.min(1, vis); ctx.fillStyle = c.color || '#ef4444'; ctx.fill(); ctx.restore(); }
    else { ctx.fillStyle = 'rgba(148,163,184,.25)'; ctx.fill(); }
    ctx.strokeStyle = c.color || '#ef4444'; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(9, -9); ctx.lineTo(9, 9); ctx.stroke();
    ctx.strokeStyle = c.color || '#ef4444'; ctx.lineWidth = 1.2 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(2, -11); ctx.lineTo(7, -16); ctx.moveTo(7, -16); ctx.lineTo(4, -15); ctx.moveTo(7, -16); ctx.lineTo(6, -13); ctx.stroke();
  };
  CircuitSim.prototype._drawDiode = function (ctx, bodyCol) {
    ctx.beginPath(); ctx.moveTo(-9, -9); ctx.lineTo(-9, 9); ctx.lineTo(9, 0); ctx.closePath(); ctx.fillStyle = bodyCol; ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(9, -9); ctx.lineTo(9, 9); ctx.stroke();
  };
  CircuitSim.prototype._drawCapacitor = function (ctx) {
    ctx.lineWidth = 2 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(-4, -11); ctx.lineTo(-4, 11); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(4, -11); ctx.lineTo(4, 11); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-14, 0); ctx.lineTo(-4, 0); ctx.moveTo(4, 0); ctx.lineTo(14, 0); ctx.stroke();
  };
  CircuitSim.prototype._drawInductor = function (ctx) {
    ctx.lineWidth = 2 / this.cam.s; ctx.beginPath();
    for (var i = 0; i < 4; i++) { var cx = -12 + i * 8; ctx.moveTo(cx, 0); ctx.arc(cx + 4, 0, 4, Math.PI, 0, false); }
    ctx.stroke();
  };
  CircuitSim.prototype._drawSwitch = function (ctx, c) {
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath(); ctx.arc(-12, 0, 2.6, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(12, 0, 2.6, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.moveTo(-12, 0);
    if (c.closed) ctx.lineTo(12, 0); else ctx.lineTo(9, -12);
    ctx.stroke();
  };
  CircuitSim.prototype._drawFuse = function (ctx, c, rc) {
    ctx.lineWidth = 2 / this.cam.s;
    ctx.strokeRect(-12, -6, 24, 12);
    if (c.blown || (rc && rc.state === 'blown')) {
      ctx.beginPath(); ctx.moveTo(-10, 0); ctx.lineTo(-2, 0); ctx.moveTo(2, 0); ctx.lineTo(10, 0);
      ctx.moveTo(-2, -3); ctx.lineTo(2, 3); ctx.moveTo(-2, 3); ctx.lineTo(2, -3); ctx.stroke();
    } else { ctx.beginPath(); ctx.moveTo(-10, 0); ctx.lineTo(10, 0); ctx.stroke(); }
  };
  CircuitSim.prototype._drawCurrentSource = function (ctx, c) {
    ctx.lineWidth = 2 / this.cam.s; ctx.beginPath(); ctx.arc(0, 0, 12, 0, Math.PI * 2); ctx.stroke();
    // arrow up = current direction
    ctx.beginPath(); ctx.moveTo(0, 7); ctx.lineTo(0, -7); ctx.moveTo(-3, -3); ctx.lineTo(0, -7); ctx.lineTo(3, -3); ctx.stroke();
  };
  CircuitSim.prototype._drawLamp = function (ctx, c, rc) {
    var glow = rc && rc.brightness ? rc.brightness : 0;
    if (glow > 0.02) { ctx.save(); ctx.globalAlpha = Math.min(0.6, glow * 0.7); ctx.fillStyle = c.color || '#fbbf24'; ctx.beginPath(); ctx.arc(0, 0, 16, 0, Math.PI * 2); ctx.fill(); ctx.restore(); }
    ctx.lineWidth = 2 / this.cam.s; ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-7, -7); ctx.lineTo(7, 7); ctx.moveTo(-7, 7); ctx.lineTo(7, -7); ctx.stroke();
  };
  CircuitSim.prototype._drawBuzzer = function (ctx, rc) {
    ctx.lineWidth = 2 / this.cam.s;
    ctx.beginPath(); ctx.arc(0, 0, 11, Math.PI * 0.5, Math.PI * 1.5, false); ctx.lineTo(6, -11); ctx.lineTo(6, 11); ctx.closePath(); ctx.stroke();
    if (rc && rc.active) { ctx.save(); ctx.globalAlpha = 0.5 + 0.4 * Math.sin(this.phase * 0.4); ctx.beginPath(); ctx.arc(9, 0, 15, -0.6, 0.6); ctx.stroke(); ctx.restore(); }
  };
  CircuitSim.prototype._drawMotor = function (ctx, rc) {
    ctx.lineWidth = 2 / this.cam.s; ctx.beginPath(); ctx.arc(0, 0, 12, 0, Math.PI * 2); ctx.stroke();
    ctx.save();
    if (rc && rc.active) ctx.rotate((this.phase * 0.12) % (Math.PI * 2));
    ctx.font = 'bold ' + (13 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = ctx.strokeStyle;
    ctx.fillText('M', 0, 0.5); ctx.restore(); ctx.textBaseline = 'alphabetic';
  };
  CircuitSim.prototype._drawTransistor = function (ctx, c, rc, bodyCol, lead) {
    var npn = c.kind !== 'pnp';
    var on = rc && rc.state === 'on';
    ctx.strokeStyle = lead; ctx.lineWidth = 2 / this.cam.s;
    // base lead (left) to the vertical bar at x=-4
    ctx.beginPath(); ctx.moveTo(-SPAN, 0); ctx.lineTo(-6, 0); ctx.stroke();
    ctx.strokeStyle = on ? (c.color || '#c084fc') : bodyCol;
    ctx.beginPath(); ctx.moveTo(-6, -12); ctx.lineTo(-6, 12); ctx.stroke(); // base bar
    // collector (top-right) and emitter (bottom-right) diagonals from base bar
    ctx.beginPath(); ctx.moveTo(-6, -5); ctx.lineTo(10, -14); ctx.moveTo(-6, 5); ctx.lineTo(10, 14); ctx.stroke();
    ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(10, -14); ctx.lineTo(SPAN, -SPAN); ctx.moveTo(10, 14); ctx.lineTo(SPAN, SPAN); ctx.stroke();
    // emitter arrow
    ctx.strokeStyle = on ? (c.color || '#c084fc') : bodyCol; ctx.fillStyle = ctx.strokeStyle;
    if (npn) { ctx.beginPath(); ctx.moveTo(10, 14); ctx.lineTo(4, 11); ctx.lineTo(7, 8); ctx.closePath(); ctx.fill(); }
    else { ctx.beginPath(); ctx.moveTo(-6, 5); ctx.lineTo(0, 6); ctx.lineTo(-2, 11); ctx.closePath(); ctx.fill(); }
  };
  CircuitSim.prototype._drawButton = function (ctx, c, rc) {
    var pressed = rc ? rc.state === 'pressed' : !!c.pressed;
    ctx.lineWidth = 2 / this.cam.s; ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath(); ctx.moveTo(-14, 0); ctx.lineTo(-7, 0); ctx.moveTo(7, 0); ctx.lineTo(14, 0); ctx.stroke(); // contacts (gap)
    ctx.beginPath(); ctx.arc(-7, 0, 1.8, 0, Math.PI * 2); ctx.arc(7, 0, 1.8, 0, Math.PI * 2); ctx.fill();
    var y = pressed ? -2 : -7;
    ctx.beginPath(); ctx.moveTo(-9, y); ctx.lineTo(9, y); ctx.stroke();          // movable bar
    if (pressed) { ctx.beginPath(); ctx.moveTo(-7, 0); ctx.lineTo(-7, y); ctx.moveTo(7, 0); ctx.lineTo(7, y); ctx.stroke(); }
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(0, -13); ctx.stroke();          // plunger
    ctx.beginPath(); ctx.moveTo(-6, -13); ctx.lineTo(6, -13); ctx.stroke();       // cap
  };
  CircuitSim.prototype._drawPhoto = function (ctx, c) {
    this._drawResistor(ctx);
    ctx.lineWidth = 1.4 / this.cam.s;
    ctx.beginPath(); ctx.arc(0, 0, 15, 0, Math.PI * 2); ctx.stroke();
    // two arrows pointing in (incident light)
    ctx.beginPath(); ctx.moveTo(-22, -20); ctx.lineTo(-11, -11); ctx.lineTo(-14, -11); ctx.moveTo(-11, -11); ctx.lineTo(-11, -14); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-16, -24); ctx.lineTo(-5, -15); ctx.lineTo(-8, -15); ctx.moveTo(-5, -15); ctx.lineTo(-5, -18); ctx.stroke();
  };
  CircuitSim.prototype._drawThermistor = function (ctx, c) {
    this._drawResistor(ctx);
    ctx.lineWidth = 1.6 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(-15, 11); ctx.lineTo(-9, 11); ctx.lineTo(13, -11); ctx.stroke(); // thermistor slash with a foot
  };
  CircuitSim.prototype._drawRelay = function (ctx, c, rc, bodyCol, lead) {
    var energ = rc && rc.energized;
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(-SPAN, -20); ctx.lineTo(-16, -20); ctx.moveTo(-SPAN, 20); ctx.lineTo(-16, 20); ctx.stroke(); // coil leads
    ctx.strokeStyle = energ ? (c.color || '#60a5fa') : bodyCol;
    ctx.strokeRect(-16, -20, 12, 40);   // coil box
    ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(SPAN, -20); ctx.lineTo(16, -20); ctx.moveTo(SPAN, 20); ctx.lineTo(16, 20); ctx.stroke(); // contact leads
    ctx.fillStyle = lead; ctx.beginPath(); ctx.arc(16, -20, 2, 0, Math.PI * 2); ctx.arc(16, 20, 2, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = energ ? (c.color || '#60a5fa') : bodyCol;
    ctx.beginPath(); ctx.moveTo(16, -20); if (energ) ctx.lineTo(16, 20); else ctx.lineTo(7, 8); ctx.stroke(); // swinging contact
    ctx.save(); ctx.setLineDash([3 / this.cam.s, 3 / this.cam.s]); ctx.strokeStyle = bodyCol; ctx.lineWidth = 1 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(-4, 0); ctx.lineTo(13, 0); ctx.stroke(); ctx.restore(); // mechanical coupling
  };
  CircuitSim.prototype._drawOpamp = function (ctx, c, rc, bodyCol, lead) {
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(-SPAN, -20); ctx.lineTo(-18, -20); ctx.moveTo(-SPAN, 20); ctx.lineTo(-18, 20); ctx.moveTo(18, 0); ctx.lineTo(SPAN, 0); ctx.stroke();
    ctx.strokeStyle = c.color || bodyCol;
    ctx.beginPath(); ctx.moveTo(-18, -24); ctx.lineTo(-18, 24); ctx.lineTo(18, 0); ctx.closePath(); ctx.stroke();
    ctx.fillStyle = ctx.strokeStyle; ctx.font = (12 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('+', -11, -13); ctx.fillText('−', -11, 13); ctx.textBaseline = 'alphabetic';
  };
  CircuitSim.prototype._drawLogic = function (ctx, c, rc, bodyCol, lead) {
    var isNot = c.type === 'logic_not', kind = isNot ? 'not' : (c.kind || 'and');
    var high = rc && rc.logicHigh, bubble = (kind === 'not' || kind === 'nand' || kind === 'nor');
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead;
    if (isNot) { ctx.beginPath(); ctx.moveTo(-SPAN, 0); ctx.lineTo(-15, 0); ctx.stroke(); }
    else { ctx.beginPath(); ctx.moveTo(-SPAN, -20); ctx.lineTo(-17, -20); ctx.moveTo(-SPAN, 20); ctx.lineTo(-17, 20); ctx.stroke(); }
    var front = (kind === 'or' || kind === 'nor' || kind === 'xor') ? 18 : (kind === 'and' || kind === 'nand' ? 16 : 16);
    ctx.beginPath(); ctx.moveTo(bubble ? front + 5 : front, 0); ctx.lineTo(SPAN, 0); ctx.stroke();
    ctx.strokeStyle = high ? (c.color || '#2dd4bf') : bodyCol;
    ctx.beginPath();
    if (kind === 'and' || kind === 'nand') { ctx.moveTo(-16, -16); ctx.lineTo(0, -16); ctx.arc(0, 0, 16, -Math.PI / 2, Math.PI / 2); ctx.lineTo(-16, 16); ctx.closePath(); }
    else if (kind === 'not') { ctx.moveTo(-15, -16); ctx.lineTo(-15, 16); ctx.lineTo(16, 0); ctx.closePath(); }
    else { ctx.moveTo(-18, -16); ctx.quadraticCurveTo(-2, -15, 18, 0); ctx.quadraticCurveTo(-2, 15, -18, 16); ctx.quadraticCurveTo(-9, 0, -18, -16); ctx.closePath(); }
    ctx.stroke();
    if (kind === 'xor') { ctx.beginPath(); ctx.moveTo(-23, -16); ctx.quadraticCurveTo(-14, 0, -23, 16); ctx.stroke(); }
    if (bubble) { ctx.beginPath(); ctx.arc(front + 2.5, 0, 2.8, 0, Math.PI * 2); ctx.stroke(); }
  };
  CircuitSim.prototype._drawMcu = function (ctx, c, rc, bodyCol, lead, light) {
    var modes = c.pinModes || DEFAULTS.mcu.pinModes, col = c.color || '#22c55e', ys = [-20, 0, 20];
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead; ctx.beginPath();
    for (var i = 0; i < 6; i++) { var side = i < 3 ? -1 : 1, y = ys[i % 3]; ctx.moveTo(side * SPAN, y); ctx.lineTo(side * 22, y); }
    ctx.stroke();
    ctx.fillStyle = light ? 'rgba(226,232,240,.92)' : 'rgba(30,41,59,.92)'; ctx.strokeStyle = col;
    this._roundRect(ctx, -22, -28, 44, 56, 5 / this.cam.s); ctx.fill(); ctx.stroke();
    ctx.fillStyle = col; ctx.font = 'bold ' + (13 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('µC', 0, -10);
    ctx.fillStyle = light ? '#475569' : '#94a3b8'; ctx.font = (8.5 / this.cam.s) + 'px sans-serif';
    var pn = c.program || 'blink';
    ctx.fillText(t('mcu_prog_' + pn, pn).slice(0, 10), 0, 7);
    for (i = 0; i < 6; i++) {
      var s2 = i < 3 ? -1 : 1, yy = ys[i % 3], mode = modes[i];
      ctx.textAlign = s2 < 0 ? 'left' : 'right'; ctx.font = (8 / this.cam.s) + 'px sans-serif';
      ctx.fillStyle = mode === 'out' ? col : (mode === 'in' ? '#60a5fa' : '#64748b');
      ctx.fillText((i + 1) + (mode === 'out' ? ':O' : (mode === 'in' ? ':I' : '')), s2 * 19, yy + 3); // numbered pins 1-6 (Ivan)
      if (rc && rc.outStates && rc.outStates[i]) { ctx.save(); ctx.globalAlpha = 0.55; ctx.fillStyle = col; ctx.beginPath(); ctx.arc(s2 * SPAN, yy, 5, 0, Math.PI * 2); ctx.fill(); ctx.restore(); }
    }
    ctx.textBaseline = 'alphabetic';
  };
  // NE555 timer: the classic IC box. Left: TRIG/THR/DIS, right: V+/OUT/GND
  // (engine pin order 0..5 = TR,TH,DIS,OUT,V+,GND via PIN_LOCAL). OUT glows when high.
  CircuitSim.prototype._drawNe555 = function (ctx, c, rc, bodyCol, lead, light) {
    var col = c.color || '#f97316';
    var pins = PIN_LOCAL.ne555;
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead; ctx.beginPath();
    for (var i = 0; i < pins.length; i++) { var pn = pins[i]; ctx.moveTo(pn.x, pn.y); ctx.lineTo(pn.x < 0 ? -22 : 22, pn.y); }
    ctx.stroke();
    ctx.fillStyle = light ? 'rgba(226,232,240,.92)' : 'rgba(30,41,59,.92)'; ctx.strokeStyle = col;
    this._roundRect(ctx, -22, -38, 44, 76, 5 / this.cam.s); ctx.fill(); ctx.stroke();
    ctx.fillStyle = col; ctx.font = 'bold ' + (13 / this.cam.s) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('555', 0, 0);
    ctx.fillStyle = light ? '#475569' : '#94a3b8'; ctx.font = (7.5 / this.cam.s) + 'px sans-serif';
    for (i = 0; i < pins.length; i++) {
      var pl = pins[i];
      ctx.textAlign = pl.x < 0 ? 'left' : 'right';
      ctx.fillText(pl.name, pl.x < 0 ? -19 : 19, pl.y + 3);
    }
    if (rc && rc.out) { ctx.save(); ctx.globalAlpha = 0.55; ctx.fillStyle = col; ctx.beginPath(); ctx.arc(-SPAN, 10, 5, 0, Math.PI * 2); ctx.fill(); ctx.restore(); }
    ctx.textBaseline = 'alphabetic';
  };
  CircuitSim.prototype._drawMosfet = function (ctx, c, rc, bodyCol, lead) {
    var on = rc && rc.state === 'on', col = on ? (c.color || '#a3e635') : bodyCol;
    ctx.lineWidth = 2 / this.cam.s; ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(-SPAN, 0); ctx.lineTo(-10, 0); ctx.stroke();              // gate lead
    ctx.strokeStyle = col;
    ctx.beginPath(); ctx.moveTo(-10, -12); ctx.lineTo(-10, 12); ctx.stroke();             // gate plate
    ctx.beginPath(); ctx.moveTo(-4, -14); ctx.lineTo(-4, -5); ctx.moveTo(-4, -3); ctx.lineTo(-4, 3); ctx.moveTo(-4, 5); ctx.lineTo(-4, 14); ctx.stroke(); // segmented channel
    ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(-4, -10); ctx.lineTo(12, -10); ctx.lineTo(12, -14); ctx.moveTo(12, -14); ctx.lineTo(SPAN, -SPAN); ctx.stroke(); // drain
    ctx.beginPath(); ctx.moveTo(-4, 10); ctx.lineTo(12, 10); ctx.lineTo(12, 14); ctx.moveTo(12, 14); ctx.lineTo(SPAN, SPAN); ctx.stroke();     // source
    ctx.beginPath(); ctx.moveTo(-4, 0); ctx.lineTo(12, 0); ctx.lineTo(12, 10); ctx.stroke();  // body tie to source
    ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = 1.2 / this.cam.s;
    // Body-diode arrow points TOWARD the channel for an N-channel MOSFET (which is what
    // this model is: enhancement, Vth ~2 V). It used to point away from the channel —
    // the P-channel convention — so an N-channel device was drawn as a P-channel one.
    // Ivan caught it: «нарисован как п канальный». Tip now at the channel side.
    // P-channel is the opposite convention: the body-diode arrow points AWAY from
    // the channel. Same triangle, mirrored horizontally around its own span.
    if (c.kind === 'pch') {
      ctx.beginPath(); ctx.moveTo(4, 0); ctx.lineTo(-1, -3); ctx.lineTo(-1, 3); ctx.closePath(); ctx.fill(); // body arrow (p-channel: points away)
    } else {
      ctx.beginPath(); ctx.moveTo(-1, 0); ctx.lineTo(4, -3); ctx.lineTo(4, 3); ctx.closePath(); ctx.fill(); // body arrow (n-channel: points to channel)
    }
  };
  CircuitSim.prototype._drawGround = function (ctx, bodyCol, lead) {
    ctx.strokeStyle = lead;
    ctx.beginPath(); ctx.moveTo(0, -SPAN); ctx.lineTo(0, 0); ctx.stroke();
    ctx.strokeStyle = bodyCol; ctx.lineWidth = 2 / this.cam.s;
    ctx.beginPath(); ctx.moveTo(-12, 0); ctx.lineTo(12, 0); ctx.moveTo(-8, 5); ctx.lineTo(8, 5); ctx.moveTo(-4, 10); ctx.lineTo(4, 10); ctx.stroke();
  };

  CircuitSim.prototype._drawRubber = function () {
    var ctx = this.ctx;
    var from = this.pinPos(this.wiring.fromPin.id, this.wiring.fromPin.pin);
    if (!from) return;
    ctx.strokeStyle = COLORS.pinHot; ctx.lineWidth = 2.5 / this.cam.s; ctx.setLineDash([6 / this.cam.s, 4 / this.cam.s]);
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(this.wiring.x, this.wiring.y); ctx.stroke(); ctx.setLineDash([]);
  };

  /* ---------------- hit testing (world space) ---------------- */
  CircuitSim.prototype.hitPin = function (wx, wy) {
    var r = HIT_PIN / this.cam.s;
    for (var i = this.components.length - 1; i >= 0; i--) {
      var c = this.components[i], ps = this.pins(c);
      for (var p = 0; p < ps.length; p++) {
        var dx = wx - ps[p].x, dy = wy - ps[p].y;
        if (dx * dx + dy * dy <= r * r) return { id: c.id, pin: p };
      }
    }
    return null;
  };
  CircuitSim.prototype.hitComponent = function (wx, wy) {
    for (var i = this.components.length - 1; i >= 0; i--) {
      var c = this.components[i], bb = BBOX[c.type] || BBOX._def;
      var q = rotXY(wx - c.x, wy - c.y, -(c.rot || 0));
      if (Math.abs(q.x - (bb.ox || 0)) <= bb.hw && Math.abs(q.y - (bb.oy || 0)) <= bb.hh) return c;
    }
    return null;
  };
  CircuitSim.prototype.hitWire = function (wx, wy) {
    var r = HIT_WIRE / this.cam.s;
    for (var i = this.wires.length - 1; i >= 0; i--) {
      var w = this.wires[i], a = this.pinPos(w.a.id, w.a.pin), b = this.pinPos(w.b.id, w.b.pin);
      if (!a || !b) continue;
      // The wire's OWN routing flag, exactly like draw() — falling back to the
      // global toggle hit-tested an L-shaped wire as a straight diagonal, so with
      // ortho off you could not tap (and thus not delete) a right-angle wire:
      // it was selectable only while the mode it was born in was active (Ivan #6949).
      var path = this._wirePath(a, b, w.o);
      for (var k = 0; k < path.length - 1; k++) { if (this._distToSeg(wx, wy, path[k], path[k + 1]) <= r) return w; }
    }
    return null;
  };
  CircuitSim.prototype._distToSeg = function (px, py, a, b) {
    var dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
    var tt = l2 ? ((px - a.x) * dx + (py - a.y) * dy) / l2 : 0; tt = Math.max(0, Math.min(1, tt));
    var qx = a.x + tt * dx, qy = a.y + tt * dy; return Math.hypot(px - qx, py - qy);
  };

  /* ---------------- pointer events ---------------- */
  // pointer skeleton (_bindPointer/_pdown/_pmove/_pup/_pcancel/pointersActive): SimCore.pointer.
  // This tool supplies the divergent dispatch as callbacks + the double-tap handler.
  // This USED to be a dblclick listener — which the browser never delivered, because
  // the skeleton cancels pointerdown and that suppresses the compatibility mouse
  // events. The documented switch/button double-tap was dead on every input device
  // until the skeleton grew its own tap-pair detection (Ivan #6432).
  CircuitSim.prototype._onDoubleTap = function (w) {
    var c = this.hitComponent(w.x, w.y);
    if (c && c.type === 'switch') { this._beginChange(); c.closed = !c.closed; this._afterEdit(); }
    else if (c && c.type === 'button') { this._beginChange(); c.pressed = !c.pressed; this._afterEdit(); }
  };

  CircuitSim.prototype._onPointerDown = function (w, e) {
    if (this.mode === 'probe') {
      var pp = this.hitPin(w.x, w.y);
      if (pp) { this._addProbe(pp.id, pp.pin); return; }
      var pw = this.hitWire(w.x, w.y);
      if (pw) { this._addProbe(pw.a.id, pw.a.pin); return; }
      this.panning = { x: e.clientX, y: e.clientY, camx: this.cam.x, camy: this.cam.y }; this.draw(); return;
    }

    var pin = this.hitPin(w.x, w.y);
    // a junction's pin sits at its center: in select mode let the component grab win so the point stays movable
    if (pin && this.mode === 'select') { var pc0 = this._byId(pin.id); if (pc0 && pc0.type === 'junction') pin = null; }
    if (pin) { this.wiring = { fromPin: pin, x: w.x, y: w.y }; this.hotPin = null; this._hideActions(); this.draw(); return; }

    if (this.mode === 'select') {
      var c = this.hitComponent(w.x, w.y);
      if (c) { this.selected = c; this.selectedWire = null; this.drag = { comp: c, ox: w.x - c.x, oy: w.y - c.y }; this._dragSnap = this._snapshot(); this._showProps(c); this.draw(); return; }
      var wi = this.hitWire(w.x, w.y);
      if (wi) { this.selectedWire = wi; this.selected = null; this._showProps(null); this.draw(); return; }
    }
    this.panning = { x: e.clientX, y: e.clientY, camx: this.cam.x, camy: this.cam.y };
    this.selected = null; this.selectedWire = null; this._showProps(null); this._hideActions();
    this.draw();
  };

  CircuitSim.prototype._onPointerMove = function (w, e) {
    if (this.wiring) { this.wiring.x = w.x; this.wiring.y = w.y; this.hotPin = this.hitPin(w.x, w.y); this.draw(); return; }
    if (this.drag) { this.drag.comp.x = this.snap(w.x - this.drag.ox); this.drag.comp.y = this.snap(w.y - this.drag.oy); if (this.simulating || (this.scope && this.scope.running)) this._scheduleSim(); this.draw(); return; }
    if (this.panning) { this.cam.x = this.panning.camx + (e.clientX - this.panning.x); this.cam.y = this.panning.camy + (e.clientY - this.panning.y); this.draw(); return; }
    if (!this.pointersActive()) { var hp = this.hitPin(w.x, w.y); if ((hp && (!this.hotPin || hp.id !== this.hotPin.id || hp.pin !== this.hotPin.pin)) || (!hp && this.hotPin)) { this.hotPin = hp; this.draw(); } }
  };

  CircuitSim.prototype._onPointerUp = function (w, e) {
    if (this.wiring) {
      var target = this.hitPin(w.x, w.y);
      if (target && !(target.id === this.wiring.fromPin.id && target.pin === this.wiring.fromPin.pin)) {
        this._addWire(this.wiring.fromPin, target);
      } else if (!this._downMoved) {
        this._tapPin(this.wiring.fromPin);
      } else if (!this.hitComponent(w.x, w.y)) {
        // wire dragged into empty space -> bend point (Ivan: attach the wire to the grid);
        // releasing over a component body is treated as a missed pin, not a bend request
        var j = this._addJunctionAt(w.x, w.y, this.wiring.fromPin);
        if (j) this.armedPin = { id: j.id, pin: 0 };
      }
      this.wiring = null; this.hotPin = null;
    } else if (this.drag) {
      if (this._downMoved && this._dragSnap) { this._sharedView = false; this._undo.push(this._dragSnap); if (this._undo.length > 80) this._undo.shift(); this._redo = []; this._updateUndoUI(); }
      this._dragSnap = null;
      if (this.simulating || (this.scope && this.scope.running)) this._scheduleSim();
      this._persist();
    } else if (this.panning) {
      // wire-mode tap on empty canvas -> drop a junction and chain from it (tap-tap polyline)
      if (this.mode === 'wire' && !this._downMoved && !this.hitComponent(w.x, w.y) && !this.hitWire(w.x, w.y)) {
        var jn = this._addJunctionAt(w.x, w.y, this.armedPin);
        if (jn) this.armedPin = { id: jn.id, pin: 0 };
      }
      this._persist();
    }
  };

  CircuitSim.prototype._onPointerCancel = function () { this.wiring = null; this.hotPin = null; this._dragSnap = null; };

  CircuitSim.prototype._tapPin = function (pin) {
    if (this.armedPin && !(this.armedPin.id === pin.id && this.armedPin.pin === pin.pin)) {
      this._addWire(this.armedPin, pin); this.armedPin = null;
    } else if (this.armedPin && this.armedPin.id === pin.id && this.armedPin.pin === pin.pin) {
      this.armedPin = null;
    } else {
      this.armedPin = pin; this._announce(t('terminal_pick_hint', 'Tap a second terminal to finish the wire'));
    }
    this.draw();
  };

  /* ---------------- pinch / zoom ---------------- */
  // _startPinch/_movePinch/_pointerList/_dist/_wheel/zoomBy: SimCore.camera (mixed in at boot)
  CircuitSim.prototype.resetView = function () { this.cam = { x: 0, y: 0, s: 1 }; this._fit(); this._persist(); this.draw(); };
  CircuitSim.prototype._fit = function () {
    if (!this.components.length) { this.cam = { x: 0, y: 0, s: 1 }; return; }
    var minx = 1e9, miny = 1e9, maxx = -1e9, maxy = -1e9;
    for (var i = 0; i < this.components.length; i++) {
      var c = this.components[i], ps = this.pins(c);
      minx = Math.min(minx, c.x - SPAN); maxx = Math.max(maxx, c.x + SPAN); miny = Math.min(miny, c.y - SPAN); maxy = Math.max(maxy, c.y + SPAN);
      for (var p = 0; p < ps.length; p++) { minx = Math.min(minx, ps[p].x); maxx = Math.max(maxx, ps[p].x); miny = Math.min(miny, ps[p].y); maxy = Math.max(maxy, ps[p].y); }
    }
    var pad = 46, w = (maxx - minx) + pad * 2, h = (maxy - miny) + pad * 2;
    var s = Math.max(MIN_S, Math.min(MAX_S, Math.min(this.cssW / w, this.cssH / h, 1.4)));
    this.cam.s = s; this.cam.x = (this.cssW - (maxx + minx) * s) / 2; this.cam.y = (this.cssH - (maxy + miny) * s) / 2;
  };
  // content extent (world) for the shared scrollbars
  CircuitSim.prototype._scrollCfg = { h: 'csHBar', v: 'csVBar' };
  CircuitSim.prototype._modelCfg = { sel: 'csModelSel', slug: 'circuit-simulator' }; // real vs textbook current model
  CircuitSim.prototype._contentBounds = function () {
    if (!this.components.length) return null;
    var minx = 1e9, miny = 1e9, maxx = -1e9, maxy = -1e9;
    for (var i = 0; i < this.components.length; i++) {
      var c = this.components[i], ps = this.pins(c);
      minx = Math.min(minx, c.x - SPAN); maxx = Math.max(maxx, c.x + SPAN); miny = Math.min(miny, c.y - SPAN); maxy = Math.max(maxy, c.y + SPAN);
      for (var p = 0; p < ps.length; p++) { minx = Math.min(minx, ps[p].x); maxx = Math.max(maxx, ps[p].x); miny = Math.min(miny, ps[p].y); maxy = Math.max(maxy, ps[p].y); }
    }
    return { minX: minx, minY: miny, maxX: maxx, maxY: maxy };
  };

  /* ---------------- mutations ---------------- */
  CircuitSim.prototype._freeSpot = function (cx, cy, type) {
    // spiral outward over grid cells until the new part's bbox doesn't overlap anything
    var bb = BBOX[type] || BBOX._def, self = this;
    function collides(x, y) {
      for (var i = 0; i < self.components.length; i++) {
        var o = self.components[i], ob = BBOX[o.type] || BBOX._def;
        if (Math.abs((x + (bb.ox || 0)) - (o.x + (ob.ox || 0))) < bb.hw + ob.hw + 12 &&
            Math.abs((y + (bb.oy || 0)) - (o.y + (ob.oy || 0))) < bb.hh + ob.hh + 12) return true;
      }
      return false;
    }
    var sx = this.snap(cx), sy = this.snap(cy);
    if (!collides(sx, sy)) return { x: sx, y: sy };
    for (var ring = 1; ring <= 8; ring++) {
      var step = ring * GRID * 3;
      var cand = [[step, 0], [-step, 0], [0, step], [0, -step], [step, step], [-step, step], [step, -step], [-step, -step]];
      for (var k = 0; k < cand.length; k++) { var x2 = this.snap(cx + cand[k][0]), y2 = this.snap(cy + cand[k][1]); if (!collides(x2, y2)) return { x: x2, y: y2 }; }
    }
    return { x: sx, y: sy };
  };
  CircuitSim.prototype.addComponent = function (type) {
    if (!DEFAULTS[type]) return;
    this._beginChange();
    var def = JSON.parse(JSON.stringify(DEFAULTS[type] || {})); // deep clone — array defaults (mcu pinModes/outStates) must not be shared
    var cx = (this.cssW / 2 - this.cam.x) / this.cam.s, cy = (this.cssH / 2 - this.cam.y) / this.cam.s;
    var spot = this._freeSpot(cx, cy, type); // never stack a new part on an existing one (Ivan)
    var c = Object.assign({ id: 'c' + (this.nextId++), type: type, rot: 0, x: spot.x, y: spot.y }, def);
    c.ref = this._nextRef(type);
    this.components.push(c); this.selected = c; this.selectedWire = null;
    this._showProps(c); this._hideHint();
    this._ae('feature_use', { f: 'place', type: type });
    this._afterEdit();
  };
  /* Bend/junction point: a 1-pin node marker wires can end on. Created by dragging a wire
     into empty space or tapping empty canvas in wire mode; move it in select mode to bend. */
  CircuitSim.prototype._addJunctionAt = function (wx, wy, fromPin) {
    this._beginChange();
    var c = { id: 'c' + (this.nextId++), type: 'junction', rot: 0, x: this.snap(wx), y: this.snap(wy), ref: '', color: DEFAULTS.junction.color };
    this.components.push(c);
    if (fromPin && !(fromPin.id === c.id)) this.wires.push({ a: { id: fromPin.id, pin: fromPin.pin }, b: { id: c.id, pin: 0 }, o: this.ortho ? 1 : 0 });
    this._ae('feature_use', { f: 'junction' });
    this._announce(t('junction_hint', 'Bend point added — tap a terminal or empty spot to continue the wire'));
    this._hideHint();
    this._afterEdit();
    return c;
  };

  CircuitSim.prototype._addWire = function (a, b) {
    if (a.id === b.id && a.pin === b.pin) return;
    for (var i = 0; i < this.wires.length; i++) {
      var w = this.wires[i];
      if ((w.a.id === a.id && w.a.pin === a.pin && w.b.id === b.id && w.b.pin === b.pin) ||
          (w.a.id === b.id && w.a.pin === b.pin && w.b.id === a.id && w.b.pin === a.pin)) return;
    }
    this._beginChange();
    this.wires.push({ a: { id: a.id, pin: a.pin }, b: { id: b.id, pin: b.pin }, o: this.ortho ? 1 : 0 });
    this._ae('feature_use', { f: 'wire' });
    this._hideHint();
    this._afterEdit();
  };
  CircuitSim.prototype.deleteSelected = function () {
    if (this.selectedWire) {
      this._beginChange();
      var sw = this.selectedWire;
      this.wires = this.wires.filter(function (w) { return w !== sw; });
      this.selectedWire = null;
      this._ae('feature_use', { f: 'delete_wire' });
    } else if (this.selected) {
      this._beginChange();
      var id = this.selected.id;
      this.components = this.components.filter(function (c) { return c.id !== id; });
      this.wires = this.wires.filter(function (w) { return w.a.id !== id && w.b.id !== id; });
      if (this.armedPin && this.armedPin.id === id) this.armedPin = null;
      this.selected = null;
      this._ae('feature_use', { f: 'delete_comp' });
    } else return;
    this._showProps(null); this._hideActions(); this._afterEdit();
  };
  CircuitSim.prototype.rotateSelected = function () {
    if (!this.selected) return;
    this._beginChange();
    this.selected.rot = ((this.selected.rot || 0) + 90) % 360;
    this._ae('feature_use', { f: 'rotate' });
    this._afterEdit();
  };
  CircuitSim.prototype.duplicateSelected = function () {
    if (!this.selected) return;
    this._beginChange();
    var src = this.selected;
    var copy = JSON.parse(JSON.stringify(src));
    copy.id = 'c' + (this.nextId++); copy.x = this.snap(src.x + GRID * 2); copy.y = this.snap(src.y + GRID * 2);
    copy.ref = this._nextRef(src.type);
    this.components.push(copy); this.selected = copy; this.selectedWire = null;
    this._showProps(copy); this._ae('feature_use', { f: 'duplicate' });
    this._afterEdit();
  };
  CircuitSim.prototype.clearAll = function () {
    if (this.components.length || this.wires.length) this._beginChange();
    this.components = []; this.wires = []; this.selected = null; this.selectedWire = null; this.sim = null;
    this.armedPin = null; this.wiring = null;
    this.simulating = false; this.nextId = 1; this._setSimBtn(false);
    this.scopePause(); this.scope.probes = []; this._renderProbes();
    this._showProps(null); this._hideActions(); this._status('', ''); this._showHint();
    this._ae('feature_use', { f: 'clear' });
    this._stopRaf(); this._persist(); this.draw();
  };
  CircuitSim.prototype._pruneProbes = function () {
    var self = this, before = this.scope.probes.length;
    this.scope.probes = this.scope.probes.filter(function (p) { return !!self._byId(p.id); });
    if (this.scope.probes.length !== before) this._renderProbes();
  };
  CircuitSim.prototype._afterEdit = function () {
    if (this.components.length) this._hideHint(); else this._showHint();
    if (!this._sharedView) this._hideShareBanner(); // editing a preview adopts it -> drop the keep/discard banner
    this._pruneProbes();
    if (this.scope.running) this._buildTransient(); // topology changed -> restart time-domain
    if (this.simulating || (this.scope && this.scope.running)) this._scheduleSim();
    this._updateUndoUI(); this._persist(); this.draw();
  };

  /* ---------------- floating selection action bar ---------------- */
  CircuitSim.prototype._buildActions = function () {
    var el = this.actions; if (!el) return; var self = this;
    el.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
    el.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]'); if (!b) return;
      var a = b.dataset.act;
      if (a === 'rotate') self.rotateSelected();
      else if (a === 'duplicate') self.duplicateSelected();
      else if (a === 'delete') self.deleteSelected();
    });
  };
  CircuitSim.prototype._hideActions = function () { if (this.actions) this.actions.style.display = 'none'; };
  CircuitSim.prototype._positionActions = function () {
    var el = this.actions; if (!el) return;
    if (this.drag || this.panning || this.pinch || this.wiring || (!this.selected && !this.selectedWire)) { el.style.display = 'none'; return; }
    var isComp = !!this.selected, cx, cy, top;
    if (isComp) {
      var c = this.selected, bb = BBOX[c.type] || BBOX._def;
      var s1 = this.worldToCanvas(c.x, c.y);
      cx = s1.x; top = s1.y - (bb.hh + 8) * this.cam.s;
    } else {
      var wsel = this.selectedWire, a = this.pinPos(wsel.a.id, wsel.a.pin), b = this.pinPos(wsel.b.id, wsel.b.pin);
      if (!a || !b) { el.style.display = 'none'; return; }
      var mid = this.worldToCanvas((a.x + b.x) / 2, (a.y + b.y) / 2);
      cx = mid.x; top = mid.y - 12;
    }
    // toggle which buttons show (wire = delete only)
    el.setAttribute('data-mode', isComp ? 'comp' : 'wire');
    el.style.display = 'flex';
    var w = el.offsetWidth || 132, h = el.offsetHeight || 40;
    var left = cx - w / 2, tp = top - h;
    left = Math.max(6, Math.min(this.cssW - w - 6, left));
    tp = Math.max(6, Math.min(this.cssH - h - 6, tp));
    el.style.left = left + 'px'; el.style.top = tp + 'px';
  };

  /* ---------------- simulation ---------------- */
  CircuitSim.prototype.toggleSim = function () {
    this.simulating = !this.simulating;
    this._setSimBtn(this.simulating);
    if (this.simulating) { this.runSim(true); this._startRaf(); }
    else { this.sim = null; this._status('', ''); this._stopRaf(); this.draw(); }
  };
  CircuitSim.prototype._scheduleSim = function () {
    var self = this; clearTimeout(this._simT);
    this._simT = setTimeout(function () {
      if (self.simulating) self.runSim(false);
      else if (self.scope && self.scope.running) self._scopeNetSync();
    }, 80);
  };
  // Editing while the scope runs (Ivan, #6463): the running transient kept solving the OLD
  // netlist — new parts neither affected the plot nor raised "not connected". Rebuild the
  // transient when the circuit electrically changes and surface the same status Simulate shows.
  CircuitSim.prototype._scopeNetSync = function () {
    var eng = CE || window.CircuitEngine; if (!eng) return;
    var nl = this._netlist();
    if (JSON.stringify(nl) === this.scope.netKey) return;   // move/select only — keep the trace
    this._buildTransient();
    var chk = eng.simulate(nl, { ideal: !!this._ideal });
    if (!chk.ok) this._status(this._errText(chk.error, chk.refs), 'error');
    else this._status(this._statusText(chk), this._statusClass(chk));
  };
  CircuitSim.prototype._netlist = function () {
    var netComps = this.components.map(function (c) {
      var o = { id: c.id, type: c.type };
      if (c.type === 'battery') { o.voltage = c.voltage; o.rint = c.rint; }
      else if (c.type === 'ac_source') { o.voltage = c.voltage; o.freq = c.freq; o.phase = c.phase; o.rint = c.rint; }
      else if (c.type === 'bridge') { o.vf = c.vf; o.ron = c.ron; }
      else if (c.type === 'transformer') o.ratio = c.ratio;
      else if (c.type === 'resistor' || c.type === 'lamp' || c.type === 'buzzer' || c.type === 'motor') { o.resistance = c.resistance; if (c.pmax != null) o.pmax = c.pmax; }
      else if (c.type === 'potentiometer') { o.resistance = c.resistance; o.wiper = c.wiper; }
      else if (c.type === 'led') { o.vf = c.vf; o.imax = c.imax; }
      else if (c.type === 'diode') o.vf = c.vf;
      else if (c.type === 'capacitor') o.capacitance = c.capacitance;
      else if (c.type === 'inductor') o.inductance = c.inductance;
      else if (c.type === 'switch') o.closed = !!c.closed;
      else if (c.type === 'fuse') { o.imax = c.imax; o.blown = !!c.blown; }
      else if (c.type === 'current_source') o.current = c.current;
      else if (c.type === 'transistor') { o.kind = c.kind; o.hfe = c.hfe; o.icmax = c.icmax; o.pmax = c.pmax; }
      else if (c.type === 'button') o.pressed = !!c.pressed;
      else if (c.type === 'photoresistor' || c.type === 'thermistor') { o.rmin = c.rmin; o.rmax = c.rmax; o.level = c.level; }
      else if (c.type === 'relay') { o.rcoil = c.rcoil; o.pickup = c.pickup; }
      else if (c.type === 'logic_gate') { o.kind = c.kind; o.vdd = c.vdd; }
      else if (c.type === 'logic_not') o.vdd = c.vdd;
      else if (c.type === 'mcu') { o.pinModes = c.pinModes; o.outStates = c.outStates; o.vdd = c.vdd; }
      else if (c.type === 'mosfet') { o.vth = c.vth; o.ron = c.ron; o.kind = c.kind; o.idmax = c.idmax; o.vdsmax = c.vdsmax; }
      return o;
    });
    return { components: netComps, wires: this.wires.map(function (w) { return { a: { c: w.a.id, p: w.a.pin }, b: { c: w.b.id, p: w.b.pin } }; }) };
  };
  CircuitSim.prototype.runSim = function (userInitiated) {
    if (!CE) return;
    var net = this._netlist();
    var opts = { ideal: !!this._ideal };
    // When the live loop is animating AC, feed it the current wall-clock time so the
    // waveform advances; a one-off solve leaves acTime undefined and gets the peak.
    if (this._acTime != null) opts.acTime = this._acTime;
    var res = CE.simulate(net, opts);
    this.sim = res;
    // stateful fuse: a fuse that exceeded its rating blows and stays blown until reset
    if (res.ok && res.components) {
      var blewAny = false, self = this;
      this.components.forEach(function (c) { if (c.type === 'fuse' && !c.blown && res.components[c.id] && res.components[c.id].willBlow) { c.blown = true; blewAny = true; } });
      if (blewAny) { this._persist(); return this.runSim(userInitiated); }
    }
    if (!res.ok) {
      this._status(this._errText(res.error, res.refs), 'error');
      if (userInitiated) this._ae('feature_use', { f: 'simulate', ok: 0, code: res.error, ncomp: net.components.length, nwire: this.wires.length });
    } else {
      this._status(this._statusText(res), this._statusClass(res));
      if (!this.completedOnce && res.maxCurrent > 1e-6) { this.completedOnce = true; this._ae('tool_complete', { slug: 'circuit-simulator', name: 'simulate' }); }
      if (userInitiated) this._ae('feature_use', { f: 'simulate', ok: 1, ncomp: net.components.length, nwire: this.wires.length, warns: (res.warnings || []).join(',') });
    }
    this.draw();
  };
  CircuitSim.prototype._refLabel = function (id) { var c = this._byId(id); return c ? (c.ref || defaultLabel(c.type, c)) : ''; };
  // «3 kΩ / 7 kΩ» — the two arms either side of the wiper. The first number IS the
  // rheostat's effective resistance, so the row serves both wiring styles.
  CircuitSim.prototype._potEffText = function (c) {
    if (!CE) CE = window.CircuitEngine;
    var base = Math.max(+c.resistance || 10000, 2);
    var w = Math.max(0, Math.min(1, c.wiper != null ? +c.wiper : 0.5));
    var a = Math.max(base * w, 1), b = Math.max(base * (1 - w), 1);
    return (CE ? CE.fmt.R(a) : a + 'Ω') + ' / ' + (CE ? CE.fmt.R(b) : b + 'Ω');
  };
  CircuitSim.prototype._statusClass = function (res) {
    var w = res.warnings || [];
    if (w.indexOf('short_circuit') >= 0 || w.indexOf('led_overload') >= 0 || w.indexOf('fuse_blown') >= 0 || w.indexOf('overpower') >= 0 || w.indexOf('no_power_source') >= 0 || w.indexOf('open_circuit') >= 0 || w.indexOf('transistor_overheat') >= 0 || w.indexOf('transistor_overcurrent') >= 0 || w.indexOf('mosfet_overcurrent') >= 0 || w.indexOf('mosfet_overvolt') >= 0 || w.indexOf('source_dangling') >= 0) return 'warn';
    return 'ok';
  };
  CircuitSim.prototype._statusText = function (res) {
    var w = res.warnings || [], self = this;
    var has = function (x) { return w.indexOf(x) >= 0; };
    var refOf = function (code) { return self._refLabel(res.overloadRefs && res.overloadRefs[code]); };
    if (has('short_circuit')) {
      var hasLed = this.components.some(function (c) { return c.type === 'led'; });
      return hasLed ? t('warn_short_led', 'Short circuit: connect the LED through a current-limiting resistor.')
                    : t('warn_short', 'Short circuit — very high current. Add a resistor to limit it.');
    }
    if (has('led_overload')) {
      var lc = res.overloadRefs.led_overload, rr = res.components[lc], lcC = this._byId(lc);
      var lcImax = Math.max((lcC && +lcC.imax) || 0.02, 1e-4);
      // >10x the rating means nothing limits the current at all — the classic beginner trap
      // is a resistor wired IN PARALLEL with the LED (it does nothing). Say so explicitly;
      // "add or increase the resistor" reads as false when a resistor exists elsewhere.
      if (rr && Math.abs(rr.current || 0) > 10 * lcImax) {
        return (t('warn_led_no_series', 'LED {ref}: nothing limits its current — the resistor must be IN SERIES with the LED, not in parallel.')).replace('{ref}', this._refLabel(lc));
      }
      return (t('warn_led_overload', 'LED {ref} is over-driven — add or increase the resistor.')).replace('{ref}', this._refLabel(lc));
    }
    if (has('fuse_blown')) return (t('warn_fuse_blown', 'Fuse {ref} has blown — reduce the current or reset it.')).replace('{ref}', refOf('fuse_blown'));
    if (has('transistor_overcurrent')) return (t('warn_tx_overcurrent', 'Transistor {ref}: collector current exceeds its rating — reduce the load or pick a bigger transistor.')).replace('{ref}', refOf('transistor_overcurrent'));
    if (has('transistor_overheat')) return (t('warn_tx_overheat', 'Transistor {ref} is overheating (Vce·Ic above its power rating) — add a heatsink-class part or lower the current.')).replace('{ref}', refOf('transistor_overheat'));
    if (has('mosfet_overcurrent')) return (t('warn_mos_overcurrent', 'MOSFET {ref}: drain current exceeds its Id max — reduce the load or pick a bigger MOSFET.')).replace('{ref}', refOf('mosfet_overcurrent'));
    if (has('source_dangling')) return (t('warn_source_dangling', 'A terminal of {ref} leads to a dead end — the loop is not closed.')).replace('{ref}', refOf('source_dangling'));
    if (has('mosfet_overvolt')) return (t('warn_mos_overvolt', 'MOSFET {ref}: voltage exceeds its Vds max — a real part would break down.')).replace('{ref}', refOf('mosfet_overvolt'));
    if (has('overpower')) {
      // Show the NUMBERS: a bare «exceeds its power rating» reads as a false
      // alarm to someone staring at a 1 kΩ resistor (tester, 29.07: «где там
      // перегруз???» — 220 V mains across it is ~40 W vs the default 0.25 W;
      // with the figures on screen the verdict argues for itself).
      var opId = res.overloadRefs && res.overloadRefs.overpower;
      var opRc = opId != null && res.components ? res.components[opId] : null;
      var opC = opId != null ? this._byId(opId) : null;
      var opMax = Math.max((opC && +opC.pmax) || (opC && opC.type === 'resistor' ? 0.25 : 0), 1e-6);
      var opTxt = (t('warn_overpower', 'Component {ref} dissipates {p} with a {pmax} rating — use a higher-wattage part or reduce the current.'))
        .replace('{ref}', refOf('overpower'));
      if (opRc && CE) opTxt = opTxt.replace('{p}', CE.fmt.P(Math.abs(opRc.power || 0))).replace('{pmax}', CE.fmt.P(opMax));
      else opTxt = opTxt.replace(' {p}', '').replace(' {pmax}', '');
      return opTxt;
    }
    if (has('no_power_source')) return t('err_no_power_source', 'Add a battery to power the circuit.');
    if (has('open_circuit')) {
      // point at the actual break instead of a generic "open circuit"
      var openRefs = this._openPinRefs();
      if (openRefs.length) return t('err_open_pins', 'Some pins are not wired — the ones ringed in red have no path for current.') + ' (' + openRefs.slice(0, 4).join(', ') + ')';
      return t('err_open_circuit', 'Open circuit — no closed current path.');
    }
    var msg = t('sim_running', 'Simulating') + ' · ' + Object.keys(res.nodes).length + ' ' + t('node_label', 'nodes');
    if (has('no_ground')) msg += ' · ' + t('err_no_ground', 'no ground — relative voltages');
    return msg;
  };
  CircuitSim.prototype._errText = function (code, refs) {
    if (code === 'incomplete_circuit') {
      // name the parts that still have a free pin (they are ringed red on the canvas too)
      var open = this._openPinRefs();
      var names = open.length
        ? open.slice(0, 4).join(', ')
        : (refs || []).map(this._refLabel, this).filter(Boolean).slice(0, 4).join(', ');
      var base = open.length
        ? t('err_open_pins', 'Some pins are not wired — the ones ringed in red have no path for current.')
        : t('err_incomplete_circuit', 'Incomplete circuit — connect the components with wires.');
      return names ? base + ' (' + names + ')' : base;
    }
    var map = {
      short_circuit: t('err_short_circuit', 'Short circuit — a power source is connected with no resistance.'),
      singular: t('err_singular', 'Circuit cannot be solved — check the connections.')
    };
    return map[code] || t('err_singular', 'Circuit cannot be solved — check the connections.');
  };

  /* ---------------- animation loop ---------------- */
  CircuitSim.prototype._startRaf = function () {
    if (this.raf) return; var self = this; this._mcuLast = 0; this._acStart = 0;
    var step = function () {
      self.phase += 1;
      var needSolve = self.simulating && self._tickMcus();
      // Circuits with a capacitor or inductor get a REAL transient in live mode —
      // per-frame static solves treat a cap as open at DC, so a 555 blinker or a
      // multivibrator sat frozen ("мигалка не работает, а с осциллографом работает" —
      // Ivan) and a PSU's smoothing cap didn't smooth. MCU circuits keep the legacy
      // static path (their outStates mutate the netlist every tick, which would reseed
      // the transient and never let it integrate).
      if (self.simulating && self._liveNeedsTransient()) {
        self._acTime = null;
        self._stepLiveTransient();
      } else {
        if (self._liveT) { self._liveT = null; self._liveKey = null; }
        // Advance AC sources through real time and re-solve, so an AC circuit animates
        // live (a rectifier LED pulses) without needing the scope open. Throttled to the
        // frame; at 1 Hz that is smooth, at 50 Hz it reads as a steady glow, exactly as a
        // real LED would. Time is wall-clock seconds since animation started.
        if (self.simulating && self._hasAc()) {
          var nowS = ((window.performance && performance.now) ? performance.now() : 0) / 1000;
          if (!self._acStart) self._acStart = nowS;
          self._acTime = nowS - self._acStart;
          needSolve = true;
        } else {
          self._acTime = null;
        }
        if (needSolve) self.runSim(false);
      }
      self.draw();
      self.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  };
  CircuitSim.prototype._hasAc = function () {
    for (var i = 0; i < this.components.length; i++) if (this.components[i].type === 'ac_source') return true;
    return false;
  };
  CircuitSim.prototype._liveNeedsTransient = function () {
    var hasReactive = false;
    for (var i = 0; i < this.components.length; i++) {
      var tp = this.components[i].type;
      if (tp === 'mcu') return false;                       // legacy static path (see above)
      if (tp === 'capacitor' || tp === 'inductor') hasReactive = true;
    }
    return hasReactive;
  };
  // Persistent live transient: same timebase recipe as the scope (dt from _estimateTau),
  // stepped to track REAL time (capped like the scope, so fast circuits play in slow
  // motion instead of eating the frame budget). Rebuilt when the netlist electrically
  // changes; a solver failure falls back to the static path for this netlist.
  CircuitSim.prototype._stepLiveTransient = function () {
    if (!CE) CE = window.CircuitEngine;
    if (!CE) return;
    var nl = this._netlist(), key = JSON.stringify(nl);
    if (key === this._liveBadKey) { this.runSim(false); return; }   // transient failed here before
    if (!this._liveT || this._liveKey !== key) {
      var tau = this._estimateTau();
      this._liveDt = Math.max(2e-5, Math.min(5e-3, tau / 200));
      this._liveT = CE.newTransient(nl, this._liveDt, { ideal: !!this._ideal });
      this._liveKey = key;
    }
    var steps = Math.max(1, Math.min(40, Math.round(0.016 / this._liveDt)));
    var frame = null;
    for (var s = 0; s < steps; s++) {
      frame = this._liveT.step();
      if (!frame || !frame.ok) {
        this._liveT = null; this._liveKey = null; this._liveBadKey = key;
        this.runSim(false);                                  // surface the error via the static path
        return;
      }
    }
    this.sim = frame;   // shape-compatible with the DC result — readouts/glow/flow just work
  };
  CircuitSim.prototype._stopRaf = function () { if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; } this._acTime = null; this._acStart = 0; this._liveT = null; this._liveKey = null; this._liveBadKey = null; };
  // real-time "smart block" program tick — advances timed programs, re-reads inputs from the last
  // solve, updates each block's output pins, and reports whether any output changed (-> re-solve).
  CircuitSim.prototype._tickMcus = function () {
    var mcus = this.components.filter(function (c) { return c.type === 'mcu'; });
    if (!mcus.length) return false;
    var now = (window.performance && performance.now) ? performance.now() : (this._mcuLast + 16);
    var dt = this._mcuLast ? Math.min(now - this._mcuLast, 200) : 16; this._mcuLast = now;
    var changed = false, self = this;
    mcus.forEach(function (c) { if (self._stepMcu(c, dt)) changed = true; });
    return changed;
  };
  CircuitSim.prototype._stepMcu = function (c, dtMs) {
    var modes = c.pinModes || DEFAULTS.mcu.pinModes;
    if (!c.outStates || c.outStates.length < 6) c.outStates = [false, false, false, false, false, false];
    var rc = this.sim && this.sim.components ? this.sim.components[c.id] : null;
    var outIdx = [], anyIn = false, allIn = true, haveIn = false, i;
    for (i = 0; i < 6; i++) {
      if (modes[i] === 'out') outIdx.push(i);
      else if (modes[i] === 'in') { haveIn = true; var h = rc && rc.pinHigh ? !!rc.pinHigh[i] : false; if (h) anyIn = true; else allIn = false; }
    }
    var prev = c.outStates.slice(), setAll = function (v) { for (var k = 0; k < outIdx.length; k++) c.outStates[outIdx[k]] = !!v; };
    var iv = Math.max(+c.interval || 500, 20);
    switch (c.program) {
      case 'follow': setAll(haveIn ? anyIn : false); break;
      case 'invert': setAll(haveIn ? !anyIn : true); break;
      case 'and': setAll(haveIn ? allIn : false); break;
      case 'toggle': { var ni = anyIn; if (ni && !c._pin) c._latch = !c._latch; c._pin = ni; setAll(!!c._latch); break; }
      case 'sequence': { c._t = (c._t || 0) + dtMs; if (c._t >= iv) { c._t = 0; c._seq = ((c._seq || 0) + 1) % Math.max(outIdx.length, 1); } for (i = 0; i < outIdx.length; i++) c.outStates[outIdx[i]] = (i === c._seq); break; }
      case 'counter': { c._t = (c._t || 0) + dtMs; if (c._t >= iv) { c._t = 0; c._cnt = ((c._cnt || 0) + 1) % (1 << Math.max(outIdx.length, 1)); } for (i = 0; i < outIdx.length; i++) c.outStates[outIdx[i]] = !!((c._cnt >> i) & 1); break; }
      default: { c._t = (c._t || 0) + dtMs; if (c._t >= iv) { c._t = 0; c._on = !c._on; } setAll(!!c._on); break; } // blink
    }
    for (i = 0; i < 6; i++) if (c.outStates[i] !== prev[i]) return true;
    return false;
  };

  /* ---------------- oscilloscope (time-domain) ---------------- */
  CircuitSim.prototype._bindScope = function () {
    var self = this;
    var tog = this.$('csScopeToggle'); if (tog) tog.addEventListener('click', function () { self.scopeSetOpen(!self.scope.open); });
    var run = this.$('csScopeRun'); if (run) run.addEventListener('click', function () { if (self.scope.running) self.scopePause(); else self.scopeRun(); });
    var rst = this.$('csScopeReset'); if (rst) rst.addEventListener('click', function () { self.scopeReset(); });
    var addp = this.$('csScopeAddProbe'); if (addp) addp.addEventListener('click', function () { self.setMode(self.mode === 'probe' ? 'select' : 'probe'); });
    var demo = this.$('csScopeDemo'); if (demo) demo.addEventListener('click', function () { self.scopeDemo(); });
    var chips = this.$('csScopeProbes'); if (chips) chips.addEventListener('click', function (e) { var b = e.target.closest('[data-probe]'); if (b) self._removeProbe(+b.dataset.probe); });
    this.scopeCanvas = this.$('csScopeCanvas');
    if (window.ResizeObserver && this.scopeCanvas) { var rt = 0; this._sro = new ResizeObserver(function () { clearTimeout(rt); rt = setTimeout(function () { self._scopeResize(); }, 60); }); this._sro.observe(this.scopeCanvas.parentNode); }
    this._renderProbes();
  };
  // scope subsystem (scopeSetOpen/_buildTransient/scopeRun/Pause/Reset/_scopeStep/_removeProbe/
  // _renderProbes/_scopeUiState/_scopeResize/_drawScope): SimCore.scope. Tool seams:
  CircuitSim.prototype._scopeCfg = { toggle: 'csScopeToggle', body: 'csScopeBody', run: 'csScopeRun', addProbe: 'csScopeAddProbe', probes: 'csScopeProbes', chip: 'cs-scope-chip', hint: 'cs-scope-hint' };
  CircuitSim.prototype._scopeSampleV = function (frame, pr) { var node = frame.pinNodes[pr.id + '#' + pr.pin]; return (node != null && frame.nodes[node] != null) ? frame.nodes[node] : 0; };
  CircuitSim.prototype._scopeStopLive = function () { this.simulating = false; this._setSimBtn(false); this._stopRaf(); this._status('', ''); };
  CircuitSim.prototype._estimateTau = function () {
    // An AC circuit's time base comes from the SOURCE PERIOD, not from RC. Sizing the window
    // by the smoothing capacitor's tau (1 kΩ × 1000 µF = 1 s → a 6 s window) crushed 300 mains
    // cycles into a solid block — you could not see a single hump of the rectified wave. A few
    // periods keep the ripple readable, and because the scope scrolls, once the capacitor has
    // charged you see the ripple riding on the settled DC — exactly the lesson.
    var fmin = 0;
    for (var i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      if (c.type !== 'ac_source') continue;
      var f = Math.max(+c.freq || 50, 1e-3);
      if (!fmin || f < fmin) fmin = f;
    }
    if (fmin) return (1 / fmin) * 0.8;   // scope uses tau*6 for the window -> ~5 periods

    var Rs = [];
    this.components.forEach(function (c) {
      if (c.type === 'resistor' || c.type === 'lamp' || c.type === 'motor' || c.type === 'buzzer') Rs.push(Math.max(+c.resistance || 1000, 1));
      else if (c.type === 'potentiometer') Rs.push(Math.max((+c.resistance || 10000) * (c.wiper != null ? +c.wiper : 0.5), 1));
    });
    var Rrep = 1000;
    if (Rs.length) { Rs.sort(function (a, b) { return a - b; }); Rrep = Rs[Math.floor(Rs.length / 2)]; }
    var taus = [];
    this.components.forEach(function (c) {
      if (c.type === 'capacitor') taus.push(Rrep * Math.max(+c.capacitance || 100, 1) * 1e-6);
      else if (c.type === 'inductor') taus.push(Math.max(+c.inductance || 100, 1) * 1e-6 / Math.max(Rrep, 0.1));
    });
    return taus.length ? Math.max.apply(null, taus) : 0.05;
  };
  // one-tap walkthrough: load the RC example, probe the capacitor and run — "so THAT's what it does"
  CircuitSim.prototype.scopeDemo = function () {
    this.loadExample('rc');
    var cap = null;
    for (var i = 0; i < this.components.length; i++) if (this.components[i].type === 'capacitor') { cap = this.components[i]; break; }
    if (!cap) return;
    this.scope.probes = [];
    if (!this.scope.open) this.scopeSetOpen(true);
    this._addProbe(cap.id, 0);
    this.scopeRun();
    this._ae('feature_use', { f: 'scope_demo' });
  };
  CircuitSim.prototype._addProbe = function (id, pin) {
    for (var i = 0; i < this.scope.probes.length; i++) { var p = this.scope.probes[i]; if (p.id === id && p.pin === pin) { this._removeProbe(i); return; } }
    if (this.scope.probes.length >= 6) { if (window.__toast) window.__toast(t('scope_max', 'Up to 6 probes'), 'warning'); return; }
    var used = {}; this.scope.probes.forEach(function (p) { used[p.color] = 1; });
    var color = this._scopeColors[this.scope.probes.length % this._scopeColors.length];
    for (var k = 0; k < this._scopeColors.length; k++) { if (!used[this._scopeColors[k]]) { color = this._scopeColors[k]; break; } }
    var c = this._byId(id), label = c ? (c.ref || defaultLabel(c.type, c)) : id;
    this.scope.probes.push({ id: id, pin: pin, color: color, label: label, samples: [] });
    if (!this.scope.open) this.scopeSetOpen(true);
    this._renderProbes(); this._ae('feature_use', { f: 'scope_probe' }); this.draw();
  };

  /* ---------------- examples ---------------- */
  CircuitSim.prototype.loadExample = function (ex) {
    this._beginChange();
    this.scopePause(); this.scope.probes = []; this._renderProbes();
    this.components = []; this.wires = []; this.nextId = 1; this.selected = null; this.selectedWire = null;
    var self = this;
    function add(type, x, y, extra) { var c = Object.assign({ id: 'c' + (self.nextId++), type: type, rot: 0, x: x, y: y }, JSON.parse(JSON.stringify(DEFAULTS[type] || {})), extra || {}); c.ref = self._nextRef(type); self.components.push(c); return c.id; }
    function wire(a, pa, b, pb) { self.wires.push({ a: { id: a, pin: pa }, b: { id: b, pin: pb }, o: 1 }); }
    // Textbook layouts: battery vertical on the left (rot 90 -> "+" on top), the chain runs
    // left-to-right along a top rail, every tail drops STRAIGHT DOWN into its own ground
    // symbol (grounds are one node) — so orthogonal wires are straight lines, no zigzags.
    if (ex === 'led') {
      var b = add('battery', 80, 160, { voltage: 9, rot: 90 });
      var r = add('resistor', 200, 120, { resistance: 330 });
      var d = add('led', 360, 120, { vf: 2.0 });
      var g1 = add('ground', 80, 280), g2 = add('ground', 400, 200);
      wire(b, 0, r, 0); wire(r, 1, d, 0); wire(d, 1, g2, 0); wire(b, 1, g1, 0);
    } else if (ex === 'divider') {
      var b2 = add('battery', 80, 160, { voltage: 12, rot: 90 });
      var r1 = add('resistor', 200, 120, { resistance: 10000 });
      var r2 = add('resistor', 320, 160, { resistance: 5600, rot: 90 });
      var g3 = add('ground', 80, 280), g4 = add('ground', 320, 280);
      wire(b2, 0, r1, 0); wire(r1, 1, r2, 0); wire(r2, 1, g4, 0); wire(b2, 1, g3, 0);
    } else if (ex === 'series') {
      var b3 = add('battery', 80, 160, { voltage: 12, rot: 90 });
      var rr = add('resistor', 200, 120, { resistance: 400 });
      var d1 = add('led', 360, 120, { vf: 2.0, color: '#ef4444' });
      var d2 = add('led', 520, 120, { vf: 2.0, color: '#10b981' });
      var g5 = add('ground', 80, 280), g6 = add('ground', 560, 200);
      wire(b3, 0, rr, 0); wire(rr, 1, d1, 0); wire(d1, 1, d2, 0); wire(d2, 1, g6, 0); wire(b3, 1, g5, 0);
    } else if (ex === 'rc') {
      var b4 = add('battery', 80, 160, { voltage: 5, rot: 90 });
      var s4 = add('switch', 200, 120, { closed: true });
      var r4 = add('resistor', 360, 120, { resistance: 1000 });
      var c4 = add('capacitor', 520, 160, { capacitance: 100, rot: 90 });
      var g7 = add('ground', 80, 280), g8 = add('ground', 520, 280);
      wire(b4, 0, s4, 0); wire(s4, 1, r4, 0); wire(r4, 1, c4, 0); wire(c4, 1, g8, 0); wire(b4, 1, g7, 0);
    } else if (ex === 'rectifier') {
      // Mains-style rectifier: AC in -> full-wave bridge -> smoothing cap -> load.
      // Run the oscilloscope and probe the AC node vs the capacitor to see the sine
      // turn into (nearly) flat DC — the whole point of the bridge.
      var acv = add('ac_source', 80, 200, { voltage: 10, freq: 50, rot: 90 });
      var brd = add('bridge', 260, 200);
      var cs1 = add('capacitor', 430, 240, { capacitance: 1000, rot: 90 });
      var rl1 = add('resistor', 560, 160, { resistance: 1000 });
      var gr1 = add('ground', 430, 340);
      wire(acv, 0, brd, 0); wire(acv, 1, brd, 1);
      wire(brd, 2, cs1, 0); wire(cs1, 1, brd, 3);
      wire(brd, 2, rl1, 0); wire(rl1, 1, brd, 3);
      wire(brd, 3, gr1, 0);
    } else if (ex === 'psu') {
      // Full linear power supply: mains AC -> step-down transformer -> full-wave bridge ->
      // smoothing capacitor -> DC load. 20V peak in, N=2 -> 10V peak, bridge -> ~8.6V smooth DC.
      // Run the scope and probe the transformer secondary vs the capacitor to watch it happen.
      var pac = add('ac_source', 80, 200, { voltage: 20, freq: 50, rot: 90 });
      var pxf = add('transformer', 240, 200, { ratio: 2 });
      var pbr = add('bridge', 420, 200);
      var pcap = add('capacitor', 580, 240, { capacitance: 1000, rot: 90 });
      var prl = add('resistor', 710, 160, { resistance: 1000 });
      var pgr = add('ground', 580, 340);
      wire(pac, 0, pxf, 0); wire(pac, 1, pxf, 1);           // mains into primary
      wire(pxf, 2, pbr, 0); wire(pxf, 3, pbr, 1);           // secondary into bridge AC inputs
      wire(pbr, 2, pcap, 0); wire(pcap, 1, pbr, 3);         // smoothing cap across DC rails
      wire(pbr, 2, prl, 0); wire(prl, 1, pbr, 3);           // load across DC rails
      wire(pbr, 3, pgr, 0);                                 // ground the negative rail
    } else if (ex === 'ne555') {
      // Classic 555 astable LED blinker: Ra=Rb=10k, C=100uF -> ~2.1 s period, 2/3 duty.
      // Run the scope on the capacitor to watch it bounce between 1/3 and 2/3 Vcc.
      var nb = add('battery', 80, 200, { voltage: 9, rot: 90 });
      var nu = add('ne555', 340, 200);
      var nra = add('resistor', 200, 100, { resistance: 10000 });
      var nrb = add('resistor', 200, 180, { resistance: 10000, rot: 90 });
      var nc = add('capacitor', 200, 320, { capacitance: 100, rot: 90 });
      var nrl = add('resistor', 520, 160, { resistance: 330 });
      var nled = add('led', 640, 220, { vf: 2.0, rot: 90 });
      var ng = add('ground', 80, 340);
      wire(nb, 0, nra, 0); wire(nb, 0, nu, 4);          // +9V to Ra and VCC
      wire(nra, 1, nu, 2); wire(nra, 1, nrb, 0);        // Ra -> DIS node -> Rb
      wire(nrb, 1, nu, 0); wire(nu, 0, nu, 1);          // Rb -> TRIG, TRIG=THR
      wire(nrb, 1, nc, 0); wire(nc, 1, nb, 1);          // timing cap to the rail
      wire(nu, 5, nb, 1); wire(nb, 1, ng, 0);           // chip GND + ground
      wire(nu, 3, nrl, 0); wire(nrl, 1, nled, 0); wire(nled, 1, nb, 1);  // OUT -> R -> LED
    } else if (ex === 'multivib') {
      // Adjustable astable multivibrator — laid out as the TEXTBOOK symmetric
      // ladder (Ivan #6957: «сделайте мультивибратор похожим на мультивибратор»):
      // top +9V rail; four verticals hanging off it — collector load (R+LED) /
      // base pot / base pot / collector load; the two NPNs at the bottom; the
      // 10uF cross-coupling caps horizontal in the middle row. Same wiring as
      // before (the oscillation is proven by oracle C22) — only geometry moved.
      var mb = add('battery', 80, 240, { voltage: 9, rot: 90 });
      var mr1 = add('resistor', 220, 160, { resistance: 470, rot: 90 });
      var ml1 = add('led', 220, 260, { vf: 2.0, rot: 90, color: '#ef4444' });
      var mp1 = add('potentiometer', 400, 160, { resistance: 100000, wiper: 0.5, rot: 90 });
      var mp2 = add('potentiometer', 560, 160, { resistance: 100000, wiper: 0.5, rot: 90 });
      var mr2 = add('resistor', 740, 160, { resistance: 470, rot: 90 });
      var ml2 = add('led', 740, 260, { vf: 2.0, rot: 90, color: '#10b981' });
      var mq1 = add('transistor', 300, 420, { kind: 'npn' });
      var mq2 = add('transistor', 660, 420, { kind: 'npn' });
      var mc1 = add('capacitor', 440, 340, { capacitance: 10 });
      var mc2 = add('capacitor', 520, 380, { capacitance: 10 });
      var mg = add('ground', 80, 420);
      wire(mb, 0, mr1, 0); wire(mb, 0, mr2, 0);            // +9V to collector loads
      wire(mb, 0, mp1, 0); wire(mb, 0, mp2, 0);            // +9V to base pots
      wire(mr1, 1, ml1, 0); wire(ml1, 1, mq1, 0);          // load -> LED -> collector Q1
      wire(mr2, 1, ml2, 0); wire(ml2, 1, mq2, 0);
      wire(mp1, 1, mq2, 1); wire(mp2, 1, mq1, 1);          // pots feed the OPPOSITE bases
      wire(mc1, 0, mq1, 0); wire(mc1, 1, mq2, 1);          // cross-coupling caps
      wire(mc2, 0, mq2, 0); wire(mc2, 1, mq1, 1);
      wire(mq1, 2, mb, 1); wire(mq2, 2, mb, 1); wire(mb, 1, mg, 0);
    } else if (ex === 'transistor') {
      var b5 = add('battery', 80, 200, { voltage: 9, rot: 90 });
      var rl = add('resistor', 240, 160, { resistance: 470 });
      var q = add('transistor', 320, 200, { kind: 'npn' });
      var btn5 = add('button', 120, 360, { pressed: true });
      var rb = add('resistor', 280, 320, { resistance: 10000, rot: 90 });
      var g9 = add('ground', 80, 320), g10 = add('ground', 360, 320);
      wire(b5, 0, rl, 0); wire(rl, 1, q, 0);                     // + -> RL -> collector
      wire(b5, 0, btn5, 0); wire(btn5, 1, rb, 1); wire(rb, 0, q, 1); // + -> button -> Rb (up) -> base
      wire(q, 2, g10, 0); wire(b5, 1, g9, 0);                    // emitter/battery to ground rails
    } else if (ex === 'pot') {
      var b6 = add('battery', 80, 160, { voltage: 6, rot: 90 });
      var p6 = add('potentiometer', 200, 120, { resistance: 10000, wiper: 0.4 });
      var d6 = add('led', 360, 120, { vf: 2.0 });
      var g11 = add('ground', 80, 280), g12 = add('ground', 400, 200);
      wire(b6, 0, p6, 0); wire(p6, 1, d6, 0); wire(d6, 1, g12, 0); wire(b6, 1, g11, 0);
    } else if (ex === 'motor') {
      var b7 = add('battery', 80, 160, { voltage: 6, rot: 90 });
      var sw7 = add('switch', 200, 120, { closed: true });
      var m7 = add('motor', 360, 120, { resistance: 40 });
      var g13 = add('ground', 80, 280), g14 = add('ground', 400, 200);
      wire(b7, 0, sw7, 0); wire(sw7, 1, m7, 0); wire(m7, 1, g14, 0); wire(b7, 1, g13, 0);
    } else if (ex === 'relay') {
      var rb1 = add('battery', 80, 160, { voltage: 5, rot: 90 });
      var rbtn = add('button', 200, 120, { pressed: true });
      var rk = add('relay', 340, 140, { rcoil: 100, pickup: 0.02 });
      var g15 = add('ground', 80, 280), g16 = add('ground', 300, 280);
      wire(rb1, 0, rbtn, 0); wire(rbtn, 1, rk, 0);               // + -> button -> coil+
      wire(rk, 1, g16, 0); wire(rb1, 1, g15, 0);                 // coil- and battery to ground
      var rb2 = add('battery', 520, 240, { voltage: 6, rot: 90 });
      var rlmp = add('lamp', 640, 200, { resistance: 100, pmax: 1 });
      wire(rb2, 0, rk, 2); wire(rk, 3, rlmp, 0); wire(rlmp, 1, rb2, 1); // isolated contact loop
    } else if (ex === 'opamp') {
      var ob = add('battery', 80, 200, { voltage: 1.5, rot: 90 });
      var ou = add('opamp', 280, 180);
      var org = add('resistor', 240, 280, { resistance: 1000, rot: 90 });
      var orf = add('resistor', 360, 220, { resistance: 1000, rot: 90 });
      var orl = add('resistor', 440, 180, { resistance: 100 });
      var od = add('led', 560, 180, { vf: 2.0 });
      var g17 = add('ground', 80, 320), g18 = add('ground', 240, 400), g19 = add('ground', 600, 260);
      wire(ob, 0, ou, 0); wire(ob, 1, g17, 0);                   // Vin -> in+
      wire(ou, 1, org, 0); wire(org, 1, g18, 0);                 // in- -> Rg (down) -> gnd
      wire(ou, 2, orf, 0); wire(orf, 1, ou, 1);                  // out -> Rf (down-left) -> in-
      wire(ou, 2, orl, 0); wire(orl, 1, od, 0); wire(od, 1, g19, 0); // out -> R -> LED -> gnd
    } else if (ex === 'logic') {
      var lb = add('battery', 80, 200, { voltage: 5, rot: 90 });
      var lsa = add('switch', 200, 140, { closed: true });
      var lsb = add('switch', 200, 220, { closed: true });
      var lgate = add('logic_gate', 360, 180, { kind: 'and', vdd: 5 });
      var lrl = add('resistor', 480, 180, { resistance: 330 });
      var lled = add('led', 600, 180, { vf: 2.0 });
      var g20 = add('ground', 80, 320), g21 = add('ground', 640, 260);
      wire(lb, 0, lsa, 0); wire(lsa, 1, lgate, 0);               // + -> switch A -> input a
      wire(lb, 0, lsb, 0); wire(lsb, 1, lgate, 1);               // + -> switch B -> input b
      wire(lgate, 2, lrl, 0); wire(lrl, 1, lled, 0); wire(lled, 1, g21, 0);
      wire(lb, 1, g20, 0);
    } else if (ex === 'mcu_blink') {
      var mu = add('mcu', 160, 200, { program: 'blink', interval: 500, vdd: 5 });
      var mr = add('resistor', 320, 180, { resistance: 330 });
      var md = add('led', 480, 180, { vf: 2.0 });
      var mg = add('ground', 520, 260);
      wire(mu, 3, mr, 0); wire(mr, 1, md, 0); wire(md, 1, mg, 0);
    } else if (ex === 'mcu_button') {
      var bb = add('battery', 80, 160, { voltage: 5, rot: 90 });
      var bsw = add('button', 200, 120, { pressed: true });
      var bu = add('mcu', 360, 200, { program: 'follow', vdd: 5 });
      var brl = add('resistor', 520, 180, { resistance: 330 });
      var bd = add('led', 680, 180, { vf: 2.0 });
      var bg1 = add('ground', 80, 280), bg2 = add('ground', 720, 260);
      wire(bb, 0, bsw, 0); wire(bsw, 1, bu, 0);                  // + -> button -> input pin 1
      wire(bu, 3, brl, 0); wire(brl, 1, bd, 0); wire(bd, 1, bg2, 0); // output pin 4 -> R -> LED
      wire(bb, 1, bg1, 0);
    } else if (ex === 'mcu_traffic') {
      var tu = add('mcu', 120, 240, { program: 'sequence', interval: 700, vdd: 5 });
      var cols = ['#ef4444', '#f59e0b', '#10b981'];
      for (var ti = 0; ti < 3; ti++) {
        var ty = 160 + ti * 80;
        var tr = add('resistor', 280, ty, { resistance: 330 });
        var td = add('led', 440, ty, { vf: 2.0, color: cols[ti] });
        var tg2 = add('ground', 480, ty + 80);
        wire(tu, 3 + ti, tr, 0); wire(tr, 1, td, 0); wire(td, 1, tg2, 0);
      }
    }
    this.nextId = this.components.length + 1;
    this._hideHint(); this._showProps(null); this._hideActions();
    this._ae('feature_use', { f: 'example', ex: ex });
    this._fit();
    this.simulating = true; this._setSimBtn(true); this.runSim(); this._startRaf();
    this._updateUndoUI(); this._persist();
  };

  /* ---------------- properties panel ---------------- */
  // per-input apply seam for the shared _bindPropInputs loop: mA->A conversions, preset
  // side-effects, and physical value clamping (PROP_CLAMP).
  CircuitSim.prototype._clampNum = function (prop, val) { var r = PROP_CLAMP[prop]; return r ? Math.max(r[0], Math.min(r[1], val)) : val; };
  CircuitSim.prototype._applyPropInput = function (prop, val, inputType) {
    var s = this.selected; if (!s) return;
    var numeric = (inputType === 'number' || inputType === 'range');
    if (prop === '_current_ma') { s.current = this._clampNum('current', (isNaN(val) ? 20 : val) / 1000); return; }
    if (prop === '_imax_ma') { s.imax = this._clampNum('imax', (isNaN(val) ? 20 : val) / 1000); return; }
    if (prop === '_pickup_ma') { s.pickup = this._clampNum('pickup', (isNaN(val) ? 20 : val) / 1000); return; }
    if (prop === '_icmax_ma') { s.icmax = this._clampNum('icmax', (isNaN(val) ? 500 : val) / 1000); s.preset = 'custom'; return; }
    if (numeric && isNaN(val)) return; // empty field -> keep prior value
    s[prop] = numeric ? this._clampNum(prop, val) : val;
    if (s.type === 'transistor' && (prop === 'hfe' || prop === 'pmax')) s.preset = 'custom';
    if (s.type === 'mosfet' && (prop === 'vth' || prop === 'ron' || prop === 'idmax' || prop === 'vdsmax')) s.preset = 'custom';
    if (s.type === 'potentiometer' && (prop === 'wiper' || prop === 'resistance')) {
      var effEl = this.$('csPotEff'); if (effEl) effEl.textContent = this._potEffText(s);
    }
  };
  CircuitSim.prototype._showProps = function (c) {
    this._propEditing = false;
    var empty = this.$('csPropsEmpty'), content = this.$('csPropsContent');
    if (!content) return;
    if (!c) { content.style.display = 'none'; if (empty) empty.style.display = ''; return; }
    if (empty) empty.style.display = 'none'; content.style.display = '';
    var rows = [];
    var head = '<div class="cs-prop-title">' + esc((c.ref ? c.ref + ' · ' : '') + t('comp_' + c.type, c.type)) + '</div>';
    if (c.type === 'ac_source') {
      rows.push(propNum(t('prop_amplitude', 'Amplitude (V peak)'), 'voltage', c.voltage != null ? c.voltage : 10, 0.5));
      rows.push(propNum(t('prop_freq', 'Frequency (Hz)'), 'freq', c.freq != null ? c.freq : 50, 1));
      rows.push(propNum(t('prop_phase', 'Phase (°)'), 'phase', c.phase != null ? c.phase : 0, 5));
      rows.push(propNum(t('prop_rint', 'Internal resistance (Ω)'), 'rint', c.rint != null ? c.rint : 0.5, 0.1));
    }
    if (c.type === 'bridge') { rows.push(propNum(t('prop_vf', 'Forward voltage (V)'), 'vf', c.vf != null ? c.vf : 0.7, 0.1)); }
    if (c.type === 'transformer') { rows.push(propNum(t('prop_turns_ratio', 'Turns ratio (Np:Ns)'), 'ratio', c.ratio != null ? c.ratio : 2, 0.1)); }
    if (c.type === 'battery') { rows.push(propNum(t('prop_voltage', 'Voltage (V)'), 'voltage', c.voltage, 0.1)); rows.push(propNum(t('prop_rint', 'Internal resistance (Ω)'), 'rint', c.rint != null ? c.rint : 0.5, 0.1)); }
    if (c.type === 'resistor' || c.type === 'lamp' || c.type === 'buzzer' || c.type === 'motor') rows.push(propNum(t('prop_resistance', 'Resistance (Ω)'), 'resistance', c.resistance, 1));
    if (c.type === 'potentiometer') {
      rows.push(propNum(t('prop_resistance', 'Resistance (Ω)'), 'resistance', c.resistance, 1));
      rows.push(propRange(t('prop_wiper', 'Wiper'), 'wiper', c.wiper != null ? c.wiper : 0.5));
      // live Ω readout under the slider (Ivan: "сделайте чтобы отображало как меняется
      // сопротивление при передвигании ползунка") — updated on every input event
      rows.push('<div class="cs-prop-row"><label>' + esc(t('prop_pot_eff', 'Плечи движка')) + '</label><b id="csPotEff">' + esc(this._potEffText(c)) + '</b></div>');
    }
    if (c.type === 'lamp') rows.push(propNum(t('prop_pmax', 'Rated power (W)'), 'pmax', c.pmax != null ? c.pmax : 1, 0.1));
    // A real bench has ½W/1W/2W resistors — without this field the overload
    // warning was a verdict the user could not answer (Ivan #6958: his mains
    // dropper honestly dissipates ~0.5 W; the fix is a beefier resistor, and
    // now he can SAY it is one). Engine default stays 0.25 W.
    if (c.type === 'resistor') rows.push(propNum(t('prop_pmax', 'Rated power (W)'), 'pmax', c.pmax != null ? c.pmax : 0.25, 0.05));
    if (c.type === 'capacitor') rows.push(propNum(t('prop_capacitance', 'Capacitance (µF)'), 'capacitance', c.capacitance, 1));
    if (c.type === 'inductor') rows.push(propNum(t('prop_inductance', 'Inductance (µH)'), 'inductance', c.inductance, 1));
    if (c.type === 'current_source') rows.push(propNum(t('prop_current', 'Current (mA)'), '_current_ma', (c.current != null ? c.current : 0.02) * 1000, 1));
    if (c.type === 'led' || c.type === 'diode') rows.push(propNum(t('prop_vf', 'Forward Voltage (V)'), 'vf', c.vf, 0.1));
    if (c.type === 'led') { rows.push(propNum(t('prop_imax', 'Rated current (mA)'), '_imax_ma', (c.imax != null ? c.imax : 0.02) * 1000, 1)); rows.push(propColor(t('prop_color', 'LED Color'), 'color', c.color || '#ef4444')); }
    if (c.type === 'fuse') { rows.push(propNum(t('prop_imax_a', 'Rated current (A)'), 'imax', c.imax != null ? c.imax : 1, 0.1)); if (c.blown) rows.push('<div class="cs-prop-row"><label>' + esc(t('state_blown', 'Blown')) + '</label><button class="cs-tool-btn" id="csResetFuse">' + esc(t('reset_fuse', 'Reset fuse')) + '</button></div>'); }
    if (c.type === 'transistor') {
      var pk2 = c.preset || 'custom';
      rows.push('<div class="cs-prop-row"><label>' + esc(t('tx_model', 'Model')) + '</label><button class="cs-tool-btn" id="csTxPreset">' + esc(pk2 === 'custom' ? t('tx_custom', 'Custom') : pk2) + '</button></div>');
      rows.push('<div class="cs-prop-row"><label>' + esc(t('prop_kind', 'Type')) + '</label><button class="cs-tool-btn" id="csTxKind">' + esc(c.kind === 'pnp' ? 'PNP' : 'NPN') + '</button></div>');
      rows.push(propNum(t('prop_hfe', 'Gain hFE (β)'), 'hfe', c.hfe != null ? c.hfe : 100, 1));
      rows.push(propNum(t('prop_icmax', 'Max collector current (mA)'), '_icmax_ma', (c.icmax != null ? c.icmax : 0.5) * 1000, 10));
      rows.push(propNum(t('prop_pmax', 'Rated power (W)'), 'pmax', c.pmax != null ? c.pmax : 0.625, 0.05));
    }
    if (c.type === 'mosfet') {
      var mpk = c.preset || 'custom';
      rows.push('<div class="cs-prop-row"><label>' + esc(t('tx_model', 'Model')) + '</label><button class="cs-tool-btn" id="csMosPreset">' + esc(mpk === 'custom' ? t('tx_custom', 'Custom') : mpk) + '</button></div>');
      rows.push('<div class="cs-prop-row"><label>' + esc(t('prop_kind', 'Type')) + '</label><button class="cs-tool-btn" id="csMosKind">' + (c.kind === 'pch' ? 'P-MOSFET' : 'N-MOSFET') + '</button></div>');
      rows.push(propNum(t('prop_vth', 'Threshold Vgs (V)'), 'vth', c.vth != null ? c.vth : 2, 0.1));
      rows.push(propNum(t('prop_ron', 'On-resistance (Ω)'), 'ron', c.ron != null ? c.ron : 0.5, 0.1));
      rows.push(propNum(t('prop_idmax', 'Max drain current (A)'), 'idmax', c.idmax != null ? c.idmax : 5, 0.1));
      rows.push(propNum(t('prop_vdsmax', 'Max Vds (V)'), 'vdsmax', c.vdsmax != null ? c.vdsmax : 60, 1));
    }
    if (c.type === 'switch') rows.push('<div class="cs-prop-row"><label>' + esc(t('comp_switch', 'Switch')) + '</label><button class="cs-tool-btn" id="csToggleSw">' + (c.closed ? esc(t('state_closed', 'Closed')) : esc(t('state_open', 'Open'))) + '</button></div>');
    if (c.type === 'button') rows.push('<div class="cs-prop-row"><label>' + esc(t('comp_button', 'Button')) + '</label><button class="cs-tool-btn" id="csToggleBtn">' + (c.pressed ? esc(t('state_pressed', 'Pressed')) : esc(t('state_released', 'Released'))) + '</button></div>');
    if (c.type === 'photoresistor' || c.type === 'thermistor') { rows.push(propRange(c.type === 'photoresistor' ? t('prop_light', 'Light') : t('prop_temp', 'Temperature'), 'level', c.level != null ? c.level : 0.5)); rows.push(propNum(t('prop_rmin', 'Min resistance (Ω)'), 'rmin', c.rmin != null ? c.rmin : 1000, 1)); rows.push(propNum(t('prop_rmax', 'Max resistance (Ω)'), 'rmax', c.rmax != null ? c.rmax : (c.type === 'photoresistor' ? 200000 : 50000), 1)); }
    if (c.type === 'relay') { rows.push(propNum(t('prop_rcoil', 'Coil resistance (Ω)'), 'rcoil', c.rcoil != null ? c.rcoil : 100, 1)); rows.push(propNum(t('prop_pickup', 'Pickup current (mA)'), '_pickup_ma', (c.pickup != null ? c.pickup : 0.02) * 1000, 1)); }
    if (c.type === 'logic_gate') rows.push('<div class="cs-prop-row"><label>' + esc(t('prop_kind', 'Type')) + '</label><button class="cs-tool-btn" id="csGateKind">' + esc((c.kind || 'and').toUpperCase()) + '</button></div>');
    if (c.type === 'logic_gate' || c.type === 'logic_not') rows.push(propNum(t('prop_vdd', 'Logic level (V)'), 'vdd', c.vdd != null ? c.vdd : 5, 0.5));
    if (c.type === 'mcu') {
      var prog = c.program || 'blink';
      rows.push('<div class="cs-prop-row"><label>' + esc(t('mcu_program', 'Program')) + '</label><button class="cs-tool-btn" id="csMcuProg">' + esc(t('mcu_prog_' + prog, prog)) + '</button></div>');
      var modes = c.pinModes || DEFAULTS.mcu.pinModes, pinsHtml = '';
      for (var mpi = 0; mpi < 6; mpi++) { var md = modes[mpi]; pinsHtml += '<button class="cs-tool-btn cs-mcu-pin" data-mcupin="' + mpi + '">' + mpi + ':' + (md === 'out' ? 'O' : (md === 'in' ? 'I' : '·')) + '</button>'; }
      rows.push('<div class="cs-prop-row"><label>' + esc(t('mcu_pins', 'Pins (tap: in/out/off)')) + '</label><div class="cs-mcu-pins">' + pinsHtml + '</div></div>');
      if (prog === 'blink' || prog === 'sequence' || prog === 'counter') rows.push(propNum(t('mcu_interval', 'Interval (ms)'), 'interval', c.interval != null ? c.interval : 500, 50));
      rows.push(propNum(t('prop_vdd', 'Logic level (V)'), 'vdd', c.vdd != null ? c.vdd : 5, 0.5));
    }
    rows.push(propText(t('label', 'Label'), 'label', c.label != null ? c.label : ''));
    rows.push('<div class="cs-prop-actions"><button class="cs-tool-btn" id="csRotateComp">⟳ ' + esc(t('rotate', 'Rotate')) + '</button><button class="cs-tool-btn" id="csDupComp">⧉ ' + esc(t('duplicate', 'Duplicate')) + '</button><button class="cs-tool-btn danger" id="csDeleteComp">🗑️ ' + esc(t('delete', 'Delete')) + '</button></div>');
    content.innerHTML = head + rows.join('');
    var self = this;
    this._bindPropInputs(content); // shared loop -> _applyPropInput seam
    var del = this.$('csDeleteComp'); if (del) del.addEventListener('click', function () { self.deleteSelected(); });
    var rot = this.$('csRotateComp'); if (rot) rot.addEventListener('click', function () { self.rotateSelected(); });
    var dup = this.$('csDupComp'); if (dup) dup.addEventListener('click', function () { self.duplicateSelected(); });
    var swb = this.$('csToggleSw'); if (swb) swb.addEventListener('click', function () { self._beginChange(); self.selected.closed = !self.selected.closed; self._showProps(self.selected); self._afterEdit(); });
    var tx = this.$('csTxKind'); if (tx) tx.addEventListener('click', function () { self._beginChange(); self.selected.kind = self.selected.kind === 'pnp' ? 'npn' : 'pnp'; self.selected.preset = 'custom'; self._showProps(self.selected); self._afterEdit(); });
    var mk = this.$('csMosKind'); if (mk) mk.addEventListener('click', function () { self._beginChange(); self.selected.kind = self.selected.kind === 'pch' ? 'nch' : 'pch'; self.selected.preset = 'custom'; self._showProps(self.selected); self._afterEdit(); });
    var mp = this.$('csMosPreset'); if (mp) mp.addEventListener('click', function () { self._openPresetCard('mos'); });
    var txp = this.$('csTxPreset'); if (txp) txp.addEventListener('click', function () { self._openPresetCard('tx'); return;
      self._beginChange();
      var cur = self.selected.preset || 'custom', idx = 0;
      for (var i2 = 0; i2 < TX_PRESETS.length; i2++) if (TX_PRESETS[i2].key === cur) { idx = i2; break; }
      var np = TX_PRESETS[(idx + 1) % TX_PRESETS.length];
      self.selected.preset = np.key;
      if (np.kind) { self.selected.kind = np.kind; self.selected.hfe = np.hfe; self.selected.icmax = np.icmax; self.selected.pmax = np.pmax; }
      self._showProps(self.selected); self._afterEdit();
    });
    var rf = this.$('csResetFuse'); if (rf) rf.addEventListener('click', function () { self._beginChange(); self.selected.blown = false; self._showProps(self.selected); self._afterEdit(); });
    var bt = this.$('csToggleBtn'); if (bt) bt.addEventListener('click', function () { self._beginChange(); self.selected.pressed = !self.selected.pressed; self._showProps(self.selected); self._afterEdit(); });
    var gk = this.$('csGateKind'); if (gk) gk.addEventListener('click', function () { self._beginChange(); var ord = ['and', 'or', 'nand', 'nor', 'xor'], i = ord.indexOf(self.selected.kind || 'and'); self.selected.kind = ord[(i + 1) % ord.length]; self._showProps(self.selected); self._afterEdit(); });
    var mp = this.$('csMcuProg'); if (mp) mp.addEventListener('click', function () { self._beginChange(); var i = MCU_PROGRAMS.indexOf(self.selected.program || 'blink'); self.selected.program = MCU_PROGRAMS[(i + 1) % MCU_PROGRAMS.length]; self.selected._t = 0; self.selected._on = false; self.selected._seq = 0; self.selected._cnt = 0; self._showProps(self.selected); self._afterEdit(); });
    content.querySelectorAll('.cs-mcu-pin').forEach(function (btn) { btn.addEventListener('click', function () { if (!self.selected) return; self._beginChange(); var pi = +this.dataset.mcupin; var modes = (self.selected.pinModes || DEFAULTS.mcu.pinModes).slice(); var ord = ['in', 'out', 'off'], ni = (ord.indexOf(modes[pi]) + 1) % 3; modes[pi] = ord[ni]; self.selected.pinModes = modes; if (modes[pi] !== 'out' && self.selected.outStates) self.selected.outStates[pi] = false; self._showProps(self.selected); self._afterEdit(); }); });
  };
  // row builders delegate to SimCore.props (shared markup); the row class stays cs-prop-row
  function propNum(label, prop, val, step) { return SC.props._rowNum('cs-prop-row', label, prop, val, step); }
  function propText(label, prop, val) { return SC.props._rowText('cs-prop-row', label, prop, val); }
  function propColor(label, prop, val) { return SC.props._rowColor('cs-prop-row', label, prop, val); }
  function propRange(label, prop, val) { return SC.props._rowRange('cs-prop-row', label, prop, val); }
  // keep component values physical: no zero/negative R/C/L (would break the solver) and no
  // absurd magnitudes (Ivan: the tool should not let you exceed a component's sane range).
  var PROP_CLAMP = {
    resistance: [0.1, 1e11], rint: [1e-3, 1e6], capacitance: [1e-3, 1e9], inductance: [1e-3, 1e9],
    rcoil: [0.1, 1e6], rmin: [0.1, 1e9], rmax: [0.1, 1e9], hfe: [1, 1e5], pmax: [1e-3, 1e6],
    vf: [0.05, 30], vth: [0.05, 30], ron: [1e-3, 1e6], vdd: [0.5, 60], voltage: [-1e4, 1e4],
    current: [1e-6, 1e4], imax: [1e-6, 1e4], icmax: [1e-6, 1e4], pickup: [1e-6, 1e4], interval: [10, 1e7]
  };

  /* ---------------- toolbar / palette / keys ---------------- */
  CircuitSim.prototype._bindPalette = function () {
    var self = this, pal = this.$('csPalette'); if (!pal) return;
    pal.addEventListener('click', function (e) { var b = e.target.closest('.cs-comp-btn'); if (!b) return; self.addComponent(b.dataset.type); });
  };
  CircuitSim.prototype._bindToolbar = function () {
    var self = this;
    var sel = this.$('csSelectBtn'), wire = this.$('csWireBtn');
    if (sel) sel.addEventListener('click', function () { self.setMode('select'); });
    if (wire) wire.addEventListener('click', function () { self.setMode('wire'); });
    var sim = this.$('csSimBtn'); if (sim) sim.addEventListener('click', function () { self.toggleSim(); });
    var clr = this.$('csClearBtn'); if (clr) clr.addEventListener('click', function () { self.clearAll(); });
    var exp = this.$('csExportBtn'); if (exp) exp.addEventListener('click', function () { self.exportPNG(); });
    var un = this.$('csUndoBtn'); if (un) un.addEventListener('click', function () { self.undo(); });
    var re = this.$('csRedoBtn'); if (re) re.addEventListener('click', function () { self.redo(); });
    var zi = this.$('csZoomIn'); if (zi) zi.addEventListener('click', function () { self.zoomBy(1.2); });
    var zo = this.$('csZoomOut'); if (zo) zo.addEventListener('click', function () { self.zoomBy(1 / 1.2); });
    var zr = this.$('csZoomReset'); if (zr) zr.addEventListener('click', function () { self.resetView(); });
    var ob = this.$('csOrthoBtn'); if (ob) ob.addEventListener('click', function () { self.toggleOrtho(); });
    var sb = this.$('csSymStdBtn'); if (sb) sb.addEventListener('click', function () { self.toggleSymStd(); });
    var kb = this.$('csKbdBtn'); if (kb) kb.addEventListener('click', function () { self.showKbdHelp(); });
    var nt = this.$('csNetlistBtn'); if (nt) nt.addEventListener('click', function () { self.openNetlistText(); });
  };
  CircuitSim.prototype.setMode = function (m) {
    this.mode = m; this.armedPin = null;
    var sel = this.$('csSelectBtn'), wire = this.$('csWireBtn');
    if (sel) sel.classList.toggle('active', m === 'select');
    if (wire) wire.classList.toggle('active', m === 'wire');
    if (m === 'probe' && !this.scope.open) this.scopeSetOpen(true);
    this._scopeUiState();
    this._announceMode();
    this.draw();
  };
  CircuitSim.prototype._announceMode = function () {
    if (this.mode === 'wire') this._announce(t('wire_mode_hint', 'Tap two component terminals to connect them'));
    else if (this.mode === 'probe') this._announce(t('scope_probe_hint', 'Tap a wire to plot its voltage.'));
  };
  CircuitSim.prototype._bindExamples = function () {
    var self = this, ex = this.$('csExamples'); if (!ex) return;
    ex.addEventListener('click', function (e) { var b = e.target.closest('.cs-example'); if (!b) return; self.loadExample(b.dataset.ex); });
  };
  CircuitSim.prototype._bindKeys = function () {
    var self = this;
    document.addEventListener('keydown', function (e) {
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var mod = e.ctrlKey || e.metaKey;
      // Physical-key matching (e.code), NOT e.key: under a Cyrillic layout e.key for the Z
      // key is 'я', so every shortcut silently died and users had to switch layout to undo
      // (Ivan 2026-07-19). e.code is layout-independent; keep e.key as a fallback for
      // browsers/devices that don't report code.
      var K = e.code || '';
      var letter = K.indexOf('Key') === 0 ? K.charAt(3).toLowerCase() : (e.key || '').toLowerCase();
      if (mod && letter === 'z') { e.preventDefault(); if (e.shiftKey) self.redo(); else self.undo(); return; }
      if (mod && letter === 'y') { e.preventDefault(); self.redo(); return; }
      if (mod && letter === 'd') { e.preventDefault(); self.duplicateSelected(); return; }
      if (mod && letter === 'c') { e.preventDefault(); self.copySelected(); return; }
      if (mod && letter === 'v') { e.preventDefault(); self.pasteClipboard(); return; }
      if (e.key === 'Escape') { self.wiring = null; self.armedPin = null; self.selected = null; self.selectedWire = null; self._showProps(null); self._hideActions(); self.draw(); }
      else if ((e.key === 'Delete' || e.key === 'Backspace') && (self.selected || self.selectedWire)) { e.preventDefault(); self.deleteSelected(); }
      else if (letter === 'r' && self.selected) self.rotateSelected();
      else if (letter === 'w') self.setMode('wire');
      else if (letter === 'v') self.setMode('select');
      else if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        // bare-key palette shortcuts (desktop; Ivan's idea, minus Ctrl — the browser owns Ctrl+R)
        var HK = { r: 'resistor', b: 'battery', l: 'led', c: 'capacitor', d: 'diode', t: 'transistor', s: 'switch', g: 'ground', p: 'potentiometer', m: 'mcu', f: 'mosfet' };
        var ht = HK[letter];
        if (ht && !(ht === 'resistor' && self.selected)) { self.addComponent(ht); }
      }
    });
  };

  /* ---------------- export ---------------- */
  CircuitSim.prototype.exportPNG = function () {
    var self = this, cv = this.canvas;
    var done = function (blob) {
      if (!blob) return;
      if (window.__downloadFile) { window.__downloadFile(blob, 'circuit.png'); }
      else { var a = document.createElement('a'); a.download = 'circuit.png'; a.href = URL.createObjectURL(blob); a.click(); self._ae('tool_complete', { slug: 'circuit-simulator', name: 'circuit.png' }); }
      self._ae('feature_use', { f: 'export' });
    };
    if (cv.toBlob) cv.toBlob(done, 'image/png');
    else { try { var u = cv.toDataURL('image/png'); fetch(u).then(function (r) { return r.blob(); }).then(done); } catch (e) {} }
  };

  /* ---------------- orthogonal routing ---------------- */

  /* ---------------- part-model card (Ivan: "модели в отдельном окошке с характеристиками") ---------------- */
  CircuitSim.prototype._closePresetCard = function () {
    if (this._presetCard) { this._presetCard.parentNode.removeChild(this._presetCard); this._presetCard = null; }
  };
  // Gate-drive badge for a MOSFET: what rail can actually switch it fully on.
  // Rule of thumb: full enhancement needs roughly Vth + ~1.5-2 V of gate drive.
  function gateBadge(vth) {
    if (vth <= 1.1) return '3.3\u0412 / 5\u0412';
    if (vth <= 2.5) return '5\u0412';
    return '10\u0412+';
  }
  CircuitSim.prototype._openPresetCard = function (which) {
    var self = this, c = this.selected; if (!c) return;
    this._closePresetCard();
    var list = which === 'mos' ? MOS_PRESETS : TX_PRESETS;
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1300;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
    var box = document.createElement('div');
    box.style.cssText = 'background:var(--bg-secondary,#111826);border:1px solid var(--border,rgba(255,255,255,.14));border-radius:14px;max-width:560px;width:100%;max-height:80vh;overflow:auto;padding:14px;font-size:13px;color:var(--text-primary,#e2e8f0)';
    var html = '<div style="display:flex;align-items:center;margin-bottom:10px"><b style="flex:1">' + esc(t('preset_title', 'Component model')) + '</b>'
      + '<button type="button" data-close style="background:none;border:0;color:var(--text-muted,#94a3b8);font-size:18px;cursor:pointer;padding:2px 8px">&times;</button></div>';
    html += '<table style="width:100%;border-collapse:collapse;font-size:12.5px">';
    if (which === 'mos') {
      html += '<tr style="color:var(--text-muted,#94a3b8);text-align:left"><th style="padding:4px 6px"></th><th style="padding:4px 6px">N/P</th>'
        + '<th style="padding:4px 6px">Vth</th><th style="padding:4px 6px">Rds(on)</th><th style="padding:4px 6px">Id max</th><th style="padding:4px 6px">Vds max</th>'
        + '<th style="padding:4px 6px">' + esc(t('preset_gate', 'Gate from')) + '</th></tr>';
      var grouped = false;
      for (var i = 0; i < list.length; i++) {
        var m = list[i], cur = (c.preset || 'custom') === m.key;
        if (m.group === 'sanktum' && !grouped) {
          grouped = true;
          html += '<tr><td colspan="7" style="padding:8px 6px 4px;color:var(--text-muted,#94a3b8);font-size:12px">' + esc(t('preset_sanktum', 'Power N-channel \u2014 Mr.Sanktum\u2019s picks')) + '</td></tr>';
        }
        var gateCell = m.kind ? gateBadge(m.vth) : '';
        var ronCell = m.ron == null ? '' : (m.ron >= 1 ? m.ron + '\u03a9' : (Math.round(m.ron * 100000) / 100) + '\u043c\u03a9');
        html += '<tr data-pk="' + esc(m.key) + '" style="cursor:pointer;border-top:1px solid var(--border,rgba(255,255,255,.08))' + (cur ? ';background:var(--glass,rgba(255,255,255,.06))' : '') + '">'
          + '<td style="padding:6px"><b>' + esc(m.key === 'custom' ? t('tx_custom', 'Custom') : m.key) + '</b></td>'
          + (m.kind ? '<td style="padding:6px">' + (m.kind === 'pch' ? 'P' : 'N') + '</td>'
            + '<td style="padding:6px">' + m.vth + '\u0412</td>'
            + '<td style="padding:6px">' + ronCell + '</td>'
            + '<td style="padding:6px">' + m.idmax + '\u0410</td>'
            + '<td style="padding:6px">' + m.vdsmax + '\u0412</td>'
            + '<td style="padding:6px;white-space:nowrap">' + gateCell + '</td>'
            : '<td colspan="6" style="padding:6px;color:var(--text-muted,#94a3b8)"></td>')
          + '</tr>';
      }
    } else {
      html += '<tr style="color:var(--text-muted,#94a3b8);text-align:left"><th style="padding:4px 6px"></th><th style="padding:4px 6px">NPN/PNP</th>'
        + '<th style="padding:4px 6px">hFE</th><th style="padding:4px 6px">Ic max</th><th style="padding:4px 6px">P max</th></tr>';
      for (var j = 0; j < list.length; j++) {
        var tmx = list[j], cur2 = (c.preset || 'custom') === tmx.key;
        html += '<tr data-pk="' + esc(tmx.key) + '" style="cursor:pointer;border-top:1px solid var(--border,rgba(255,255,255,.08))' + (cur2 ? ';background:var(--glass,rgba(255,255,255,.06))' : '') + '">'
          + '<td style="padding:6px"><b>' + esc(tmx.key === 'custom' ? t('tx_custom', 'Custom') : tmx.key) + '</b></td>'
          + (tmx.kind ? '<td style="padding:6px">' + tmx.kind.toUpperCase() + '</td>'
            + '<td style="padding:6px">' + tmx.hfe + '</td>'
            + '<td style="padding:6px">' + (tmx.icmax >= 1 ? tmx.icmax + '\u0410' : (tmx.icmax * 1000) + '\u043c\u0410') + '</td>'
            + '<td style="padding:6px">' + tmx.pmax + '\u0412\u0442</td>'
            : '<td colspan="4" style="padding:6px;color:var(--text-muted,#94a3b8)"></td>')
          + '</tr>';
      }
    }
    html += '</table>';
    box.innerHTML = html;
    wrap.appendChild(box);
    document.body.appendChild(wrap);
    this._presetCard = wrap;
    wrap.addEventListener('click', function (e) { if (e.target === wrap || e.target.closest('[data-close]')) self._closePresetCard(); });
    box.addEventListener('click', function (e) {
      var row = e.target.closest('[data-pk]'); if (!row) return;
      var key = row.getAttribute('data-pk');
      var sel = self.selected; if (!sel) { self._closePresetCard(); return; }
      self._beginChange();
      sel.preset = key;
      for (var k2 = 0; k2 < list.length; k2++) {
        var pr = list[k2];
        if (pr.key !== key || !pr.kind) continue;
        if (which === 'mos') { sel.kind = pr.kind; sel.vth = pr.vth; sel.ron = pr.ron; sel.idmax = pr.idmax; sel.vdsmax = pr.vdsmax; }
        else { sel.kind = pr.kind; sel.hfe = pr.hfe; sel.icmax = pr.icmax; sel.pmax = pr.pmax; }
      }
      self._closePresetCard();
      self._showProps(sel); self._afterEdit();
      self._ae('feature_use', { f: 'preset_card_pick', which: which, key: key });
    });
    this._ae('feature_use', { f: 'preset_card_open', which: which });
  };

  /* ---------------- keyboard cheat-sheet (Ivan: "редко запоминаю на какую клавишу что повешено") ---------------- */
  CircuitSim.prototype.showKbdHelp = function () {
    var self = this;
    if (this._kbdCard) { this._kbdCard.parentNode.removeChild(this._kbdCard); this._kbdCard = null; return; }
    var rows = [
      ['R', t('rotate', 'Rotate')],
      ['Delete', t('delete', 'Delete')],
      ['Ctrl+Z', t('undo', 'Undo')],
      ['Ctrl+Y', t('redo', 'Redo')]
    ];
    var HK2 = { r: 'comp_resistor', b: 'comp_battery', l: 'comp_led', c: 'comp_capacitor', d: 'comp_diode', t: 'comp_transistor', s: 'comp_switch', g: 'comp_ground', p: 'comp_potentiometer', m: 'comp_mcu' };
    var addRows = '';
    for (var k in HK2) {
      if (!Object.prototype.hasOwnProperty.call(HK2, k)) continue;
      addRows += '<tr><td style="padding:3px 8px"><kbd style="background:var(--glass,rgba(255,255,255,.08));border:1px solid var(--border,rgba(255,255,255,.15));border-radius:4px;padding:1px 6px">' + k.toUpperCase() + '</kbd></td>'
        + '<td style="padding:3px 8px">+ ' + esc(t(HK2[k], k)) + '</td></tr>';
    }
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1300;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
    var html = '<div style="background:var(--bg-secondary,#111826);border:1px solid var(--border,rgba(255,255,255,.14));border-radius:14px;max-width:420px;width:100%;max-height:80vh;overflow:auto;padding:14px;font-size:13px;color:var(--text-primary,#e2e8f0)">'
      + '<div style="display:flex;align-items:center;margin-bottom:8px"><b style="flex:1">' + esc(t('kbd_title', 'Keyboard shortcuts')) + '</b>'
      + '<button type="button" data-close style="background:none;border:0;color:var(--text-muted,#94a3b8);font-size:18px;cursor:pointer;padding:2px 8px">&times;</button></div>'
      + '<table style="border-collapse:collapse">';
    for (var i2 = 0; i2 < rows.length; i2++) {
      html += '<tr><td style="padding:3px 8px"><kbd style="background:var(--glass,rgba(255,255,255,.08));border:1px solid var(--border,rgba(255,255,255,.15));border-radius:4px;padding:1px 6px">' + rows[i2][0] + '</kbd></td><td style="padding:3px 8px">' + esc(rows[i2][1]) + '</td></tr>';
    }
    html += '<tr><td colspan="2" style="padding:6px 8px;color:var(--text-muted,#94a3b8)">' + esc(t('components_title', 'Components')) + ':</td></tr>' + addRows + '</table></div>';
    wrap.innerHTML = html;
    document.body.appendChild(wrap);
    this._kbdCard = wrap;
    wrap.addEventListener('click', function (e) { if (e.target === wrap || e.target.closest('[data-close]')) { wrap.parentNode.removeChild(wrap); self._kbdCard = null; } });
    this._ae('feature_use', { f: 'kbd_help' });
  };
  /* ---------------- circuit as TEXT (Ivan: "сделал схему — перевёл в текст — открыл по тексту") ---------------- */
  CircuitSim.prototype.openNetlistText = function () {
    var self = this;
    if (this._netTextCard) { this._netTextCard.parentNode.removeChild(this._netTextCard); this._netTextCard = null; return; }
    var NT = window.CircuitNetlistText;
    if (!NT) return;
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1300;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
    var box = document.createElement('div');
    box.style.cssText = 'background:var(--bg-secondary,#111826);border:1px solid var(--border,rgba(255,255,255,.14));border-radius:14px;max-width:640px;width:100%;max-height:86vh;overflow:auto;padding:14px;font-size:13px;color:var(--text-primary,#e2e8f0)';
    box.innerHTML = '<div style="display:flex;align-items:center;margin-bottom:8px"><b style="flex:1">' + esc(t('netlist_title', 'Circuit as text')) + '</b>'
      + '<button type="button" data-close style="background:none;border:0;color:var(--text-muted,#94a3b8);font-size:18px;cursor:pointer;padding:2px 8px">&times;</button></div>'
      + '<div style="color:var(--text-muted,#94a3b8);font-size:12px;margin-bottom:8px">' + esc(t('netlist_hint', 'One connection per line: R1.1 - LED1.A. Lines with # are comments and part values. Paste a list (yours or from an AI) and press Import.')) + '</div>'
      + '<textarea data-nt style="width:100%;min-height:220px;background:var(--glass,rgba(255,255,255,.05));border:1px solid var(--border,rgba(255,255,255,.14));border-radius:10px;color:var(--text-primary,#e2e8f0);padding:10px;font:12px/1.5 ui-monospace,Consolas,monospace;resize:vertical"></textarea>'
      + '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px">'
      + '<button type="button" data-nt-import style="background:var(--accent,#3b82f6);border:0;border-radius:8px;color:#fff;padding:8px 14px;cursor:pointer;font-size:13px;font-weight:600">' + esc(t('netlist_import', 'Import')) + '</button>'
      + '<button type="button" data-nt-copy style="background:var(--glass,rgba(255,255,255,.06));border:1px solid var(--border,rgba(255,255,255,.14));border-radius:8px;color:var(--text-primary,#e2e8f0);padding:8px 14px;cursor:pointer;font-size:13px">' + esc(t('netlist_copy', 'Copy text')) + '</button>'
      + '<button type="button" data-nt-prompt style="background:var(--glass,rgba(255,255,255,.06));border:1px solid var(--border,rgba(255,255,255,.14));border-radius:8px;color:var(--text-primary,#e2e8f0);padding:8px 14px;cursor:pointer;font-size:13px">' + esc(t('netlist_prompt_copy', 'Copy the AI prompt')) + '</button>'
      + '</div>';
    wrap.appendChild(box);
    document.body.appendChild(wrap);
    this._netTextCard = wrap;
    var ta = box.querySelector('[data-nt]');
    ta.value = NT.toText(this.components, this.wires);
    var copyText = function (txt, okMsg) {
      var done = function (okc) { if (window.__toast) window.__toast(okc ? okMsg : t('share_ready', 'Ready'), okc ? 'success' : 'info'); };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(txt).then(function () { done(true); }, function () { self._fallbackCopy(txt, done); });
        else self._fallbackCopy(txt, done);
      } catch (e) { self._fallbackCopy(txt, done); }
    };
    wrap.addEventListener('click', function (e) { if (e.target === wrap || e.target.closest('[data-close]')) { wrap.parentNode.removeChild(wrap); self._netTextCard = null; } });
    box.querySelector('[data-nt-copy]').addEventListener('click', function () { copyText(ta.value, t('copied', 'Copied')); });
    box.querySelector('[data-nt-prompt]').addEventListener('click', function () { copyText(t('netlist_prompt', ''), t('copied', 'Copied')); });
    box.querySelector('[data-nt-import]').addEventListener('click', function () {
      var res = window.CircuitNetlistText.fromText(ta.value);
      if (!res.ok) { if (window.__toast) window.__toast(t('netlist_import_fail', 'Could not read the list') + (res.warnings.length ? ' (' + res.warnings[0] + ')' : ''), 'error'); return; }
      self._importNetlist(res);
      wrap.parentNode.removeChild(wrap); self._netTextCard = null;
    });
    this._ae('feature_use', { f: 'netlist_text_open' });
  };
  CircuitSim.prototype._importNetlist = function (res) {
    this._beginChange();
    this._sharedView = false;
    var comps = [], wires = [], idByRef = {}, idByJ = {}, n = 0, i;
    var place = function (type) {
      var col = n % 4, row = Math.floor(n / 4); n++;
      return { x: 120 + col * 200, y: 120 + row * 160 };
    };
    for (i = 0; i < res.components.length; i++) {
      var rc = res.components[i];
      var def = JSON.parse(JSON.stringify(DEFAULTS[rc.type] || {}));
      var pos = place(rc.type);
      var c = { id: 'c' + (i + 1), type: rc.type, ref: rc.ref, rot: 0, x: pos.x, y: pos.y };
      for (var k in def) if (Object.prototype.hasOwnProperty.call(def, k)) c[k] = def[k];
      for (var k2 in rc.props) if (Object.prototype.hasOwnProperty.call(rc.props, k2)) c[k2] = rc.props[k2];
      comps.push(c); idByRef[rc.ref] = c.id;
    }
    var jn = 0;
    var endPoint = function (e) {
      if (e.j) {
        if (!idByJ[e.j]) {
          var jc = { id: 'j' + (++jn), type: 'junction', ref: '', rot: 0, x: 140 + (jn % 5) * 90, y: 120 + Math.floor((res.components.length + 3) / 4) * 160 + 60 };
          comps.push(jc); idByJ[e.j] = jc.id;
        }
        return { id: idByJ[e.j], pin: 0 };
      }
      return { id: idByRef[e.ref], pin: e.pin };
    };
    for (i = 0; i < res.wires.length; i++) {
      var a = endPoint(res.wires[i][0]), b = endPoint(res.wires[i][1]);
      if (a.id && b.id) wires.push({ a: a, b: b, o: this.ortho ? 1 : 0 });
    }
    this.components = comps; this.wires = wires; this.nextId = comps.length + jn + 2;
    this.selected = null; this.selectedWire = null;
    this._fit(); this._hideHint(); this._persist(); this.draw();
    if (window.__toast) window.__toast(t('netlist_imported', 'Imported: parts placed on a grid — drag them into shape'), 'success');
    this._ae('feature_use', { f: 'netlist_text_import', parts: res.components.length, wires: wires.length });
  };

  CircuitSim.prototype.toggleSymStd = function () {
    this.symStd = this.symStd === 'gost' ? 'ansi' : 'gost';
    try { if (window.__toolSettings) window.__toolSettings.save('circuit-simulator', 'symStd', this.symStd); } catch (e) {}
    this._updateSymStdUI(); this._ae('feature_use', { f: 'sym_std', std: this.symStd }); this.draw();
  };
  CircuitSim.prototype._updateSymStdUI = function () {
    var b = this.$('csSymStdBtn'); if (!b) return;
    b.textContent = this.symStd === 'gost' ? t('sym_std_gost', 'ГОСТ') : 'ANSI';
    b.title = t('sym_std_hint', 'Symbol standard');
    // The PALETTE follows the standard too, not just the canvas. History: the
    // icons first MIXED the two styles (#6958), then got unified on ANSI zigzags
    // — which read as wrong-way-around on the ГОСТ default («Издеваетесь? Может
    // надо было поправить резистор, а не правильные по ГОСТу фоторезистор и
    // термистор», Ivan 30.07). Each resistor-family button carries both icons.
    var spans = document.querySelectorAll('[data-std-icon]');
    for (var i = 0; i < spans.length; i++) {
      spans[i].style.display = spans[i].getAttribute('data-std-icon') === this.symStd ? '' : 'none';
    }
  };
  CircuitSim.prototype.toggleOrtho = function () {
    this.ortho = !this.ortho;
    try { if (window.__toolSettings) window.__toolSettings.save('circuit-simulator', 'ortho', this.ortho ? 1 : 0); } catch (e) {}
    this._updateOrthoUI(); this._ae('feature_use', { f: 'ortho', on: this.ortho ? 1 : 0 }); this.draw();
  };
  CircuitSim.prototype._updateOrthoUI = function () { var b = this.$('csOrthoBtn'); if (b) b.classList.toggle('active', this.ortho); };

  /* ---------------- copy / paste ---------------- */
  CircuitSim.prototype.copySelected = function () {
    if (!this.selected) return;
    this._clipboard = JSON.parse(JSON.stringify(this.selected));
    if (window.__toast) window.__toast(t('copied', 'Copied'), 'success');
    this._ae('feature_use', { f: 'copy' });
  };
  CircuitSim.prototype.pasteClipboard = function () {
    if (!this._clipboard) { if (this.selected) this.duplicateSelected(); return; }
    this._beginChange();
    var copy = JSON.parse(JSON.stringify(this._clipboard));
    copy.id = 'c' + (this.nextId++); copy.x = this.snap(copy.x + GRID * 2); copy.y = this.snap(copy.y + GRID * 2);
    copy.ref = this._nextRef(copy.type);
    this.components.push(copy); this.selected = copy; this.selectedWire = null;
    this._showProps(copy); this._ae('feature_use', { f: 'paste' }); this._afterEdit();
  };

  /* ---------------- share by link ---------------- */
  CircuitSim.prototype._encodeCircuit = function () {
    var idIdx = {}; this.components.forEach(function (c, i) { idIdx[c.id] = i; });
    var nodes = this.components.map(function (c) { var o = {}; for (var k in c) { if (k !== 'id' && k.charAt(0) !== '_') o[k] = c[k]; } return o; });
    var wires = this.wires.map(function (w) { var arr = [idIdx[w.a.id], w.a.pin, idIdx[w.b.id], w.b.pin]; if (w.o != null) arr.push(w.o ? 1 : 0); return arr; });
    return b64e(JSON.stringify({ v: 1, n: nodes, w: wires }));
  };
  CircuitSim.prototype._decodeCircuit = function (code) {
    var json = b64d(code); if (!json) return false;
    var d; try { d = JSON.parse(json); } catch (e) { return false; }
    if (!d || !Array.isArray(d.n)) return false;
    var comps = [], self = this;
    d.n.forEach(function (o, i) { var c = Object.assign({ rot: 0 }, o); c.id = 'c' + (i + 1); comps.push(c); });
    var wires = (d.w || []).map(function (a) { var ca = comps[a[0]], cb = comps[a[2]]; if (!ca || !cb) return null; var w = { a: { id: ca.id, pin: a[1] }, b: { id: cb.id, pin: a[3] } }; if (a.length > 4 && a[4] != null) w.o = a[4] ? 1 : 0; return w; }).filter(Boolean);
    this.components = comps; this.wires = wires; this.nextId = comps.length + 1;
    comps.forEach(function (c) { if (!c.ref) c.ref = self._nextRef(c.type); });
    return true;
  };
  // share plumbing (_loadFromUrl/shareLink/_fallbackCopy/_bindShare): SimCore.share.
  // This tool supplies the payload codec + labels; the payload shape is view-specific:
  CircuitSim.prototype._shareBtnId = 'csShareBtn';
  CircuitSim.prototype._t = function (k, d) { return t(k, d); };
  CircuitSim.prototype._encodeShare = function () { return this._encodeCircuit(); };
  CircuitSim.prototype._decodeShare = function (code) { return this._decodeCircuit(code); };
  CircuitSim.prototype._isShareEmpty = function () { return !this.components.length; };
  CircuitSim.prototype._afterShareLoad = function () { this.cam = { x: 0, y: 0, s: 1 }; this._shared = true; this._showShareBanner(); };
  // explicit keep/discard prompt for a converted/shared preview (Ivan: the implicit
  // "touch it to keep" is non-obvious — make the choice a visible banner)
  CircuitSim.prototype._showShareBanner = function () { var el = this.$('csShareBanner'); if (el) el.style.display = 'flex'; };
  CircuitSim.prototype._hideShareBanner = function () { var el = this.$('csShareBanner'); if (el) el.style.display = 'none'; };
  CircuitSim.prototype._bindShareBanner = function () {
    var self = this;
    var keep = this.$('csShareKeep'); if (keep) keep.addEventListener('click', function () { self._sharedView = false; self._persist(); self._hideShareBanner(); self._ae('feature_use', { f: 'share_keep' }); });
    var disc = this.$('csShareDiscard'); if (disc) disc.addEventListener('click', function () { self._hideShareBanner(); self._sharedView = false; self.components = []; self.wires = []; self._restore(); self._afterEdit(); self._ae('feature_use', { f: 'share_discard' }); });
  };

  /* ---------------- helpers: status, hint, telemetry ---------------- */
  CircuitSim.prototype._status = function (msg, cls) {
    var el = this.$('csStatus'); if (!el) return;
    el.textContent = msg || ''; el.className = 'cs-status' + (msg ? ' cs-status-' + (cls || 'ok') : '');
    el.style.display = msg ? '' : 'none';
  };
  CircuitSim.prototype._setSimBtn = function (on) {
    var b = this.$('csSimBtn'); if (!b) return;
    b.innerHTML = on ? ('⏹ ' + esc(t('stop', 'Stop'))) : ('▶ ' + esc(t('simulate', 'Simulate')));
    b.classList.toggle('cs-sim-on', on);
  };
  CircuitSim.prototype._showHint = function () { var h = this.$('csHint'); if (h) h.style.display = this.components.length ? 'none' : ''; };
  CircuitSim.prototype._hideHint = function () { var h = this.$('csHint'); if (h) h.style.display = 'none'; };
  CircuitSim.prototype._announce = function (m) { try { if (window.__announce) window.__announce(m); } catch (e) {} };
  CircuitSim.prototype._ae = function (ev, data) { try { if (typeof window.__ae === 'function') window.__ae(ev, Object.assign({ slug: 'circuit-simulator' }, data || {})); } catch (e) {} };

  /* ---------------- shared-core mixins ---------------- */
  SC.mixin(CircuitSim.prototype, SC.camera);
  SC.mixin(CircuitSim.prototype, SC.history, ['serializeState', 'applyState', '_postHistory']);
  SC.mixin(CircuitSim.prototype, SC.share, ['_encodeShare', '_decodeShare', '_isShareEmpty', '_t']);
  if (SC.models) SC.mixin(CircuitSim.prototype, SC.models);
  SC.mixin(CircuitSim.prototype, SC.pointer, ['_onPointerDown', '_onPointerMove', '_onPointerUp']);
  SC.mixin(CircuitSim.prototype, SC.props, ['_applyPropInput']);
  SC.mixin(CircuitSim.prototype, SC.scope, ['_estimateTau', '_scopeSampleV', '_scopeStopLive']);

  /* ---------------- boot ---------------- */
  function boot() {
    if (!document.getElementById('csCanvas')) return;
    CE = window.CircuitEngine;
    var app = new CircuitSim();
    window.__circuitSim = app;
    app.init();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.CircuitSimUI = CircuitSim;
})();
